/**
 * Subject Registry · v2 NATS subject + domain warm-state surface.
 *
 * This file is the canonical home for FOUR distinct concerns that share
 * the single `SubjectRegistry` type. They are deliberately kept separate
 * — collapsing them into a single API was the R5 audit failure — and
 * each surface is anchored to a different h3 in `05-new-infra-sdk-changes.html`:
 *
 *   - T2201 / h3-L947  · FR1-FR4 invariant pins (RegistryFrInvariants)
 *   - T2202 / h3-L970  · the three primitives interface (SubjectRegistry)
 *   - T2203 / h3-L1109 · dispatch routing (subject trie + per-Subject lastSeq)
 *   - T2204 / h3-L1156 · global lifecycle listeners wired once at SDK init
 *
 * Each surface is exported below in its own clearly-marked section.
 *
 * T17120 lands the single-cap invariant that ties domain pins and per-
 * subject warm state to ONE shared RELEASE_BUFFER_CAP. Future contributors
 * must NOT add a parallel domain-level cap — see the doc block directly
 * below.
 */

import { SDK_ANON_ALLOW_SUBJECTS, SDK_USER_ALLOW_SUBJECT_TEMPLATES } from '@morfi/nats-client';
import { hashKey, type QueryClient } from '@tanstack/react-query';
import { sdkEvents } from '../../devtool/events.js';
import type {
  TrackedElementSet,
  Unbind,
  VisibilityController,
} from '../../platform/visibility-controller.js';
import type { QueryKey } from '../../types/index.js';
import { scopedSnapshotSequence } from '../scoped-snapshot-sequence.js';
import { compareCursor, extractSequenceId } from '../sequence-id.js';
import type { BoundedReplayResult } from '../wire/filter-manager.js';
import { defaultSubjectGateId } from './subject-gate-id.js';
import type { SubjectStatus } from './subject-status.js';
import {
  createTrieNode,
  dispatch as dispatchToTrie,
  getRootDedupSubjectCount,
  insertPattern,
  type Observer,
  type QueryObserverLifecycle,
  type Subject,
  subjectMatches,
  type TrieNode,
} from './subject-trie.js';

/**
 * RELEASE_BUFFER_CAP — load-bearing single-cap invariant.
 *
 * The spec discusses the cap in two places with two phrasings:
 *   - L976-977 (acquireDomain JSDoc): "subject to an LRU cap on retained domains"
 *   - L1271 (Internal mechanics card): RELEASE_BUFFER_CAP=3 with FIFO/LRU eviction
 *
 * These are THE SAME MECHANISM. One cap governs both:
 *   (a) the set of grace-eligible *domains* (whose pins are held warm), and
 *   (b) the per-subject warm state (lastSeq, observed-element registry entries,
 *       Subject/Observer maps) keyed on buffer membership.
 *
 * Do NOT add a separate domain-level cap. The buffer is the single source of truth
 * for "recently released but warm". Leave the buffer = forgotten. See T17120.
 */
export const RELEASE_BUFFER_CAP = Number(process.env.RELEASE_BUFFER_CAP ?? 3);
export const QUERY_OBSERVER_HISTORY_CAP = 128;
const SEQUENCED_EVENT_HISTORY_CAP = 256;

interface SequencedEventHistoryEntry {
  readonly subject: string;
  readonly data: Uint8Array;
  readonly sequenceId: Uint8Array;
}

// ===========================================================================
// T2206 · h3 L1208 — Connection budget · wire SUBs are fixed (JWT)
// ===========================================================================

/**
 * Connection budget snapshot. Spec file 05 §L1208:
 *
 *   Wire-level SUBs are bounded by the JWT, NOT by user behavior.
 *   The SDK opens ONE wire SUB per `allow_sub` entry. The root list is
 *   generated by `@morfi/nats-client` and includes wildcards such as
 *   `morfi.market.*.>`. Every
 *   event matching those wildcards arrives at the tab regardless of which
 *   markets are scrolled into view.
 *
 * Per-entity `acquireWhenVisible` (T2202) gates DISPATCH only — it never
 * adds or removes wire SUBs. Viewport churn only flips `logicalInterestSize`.
 *
 * Failure mode this surface prevents: duplicate per-entity SUBs on a route
 * already covered by an open wildcard — N× connection cost + dispatch
 * fan-out blow-up.
 *
 * Invariant FQ-WIRE-FIXED-001: `natsClient.subscribe(...)` may be called
 * ONLY inside the once-per-allow-sub-root init loop on this file. Hooks,
 * codegen output, and any other call site is a regression of L1208.
 */
export interface ConnectionBudget {
  /** Always 1: one WebSocket per tab. FR4 (L963). */
  readonly wsCount: 1;
  /** Constant for the session — set by the JWT's generated allow_sub roots. */
  readonly subCount: number;
  /**
   * Scales with user behavior; gates dispatch, NOT wire. Equal to the
   * count of currently-acquired per-entity interests (observer count).
   */
  readonly logicalInterestSize: number;
}

/** Aggregate, identifier-free registry counts for manual resource sampling. */
export interface SubjectRegistryResourceUsage {
  readonly observerCount: number;
  readonly trackedElements: number;
  readonly trackedEntityIds: number;
  readonly visibleEntityIds: number;
  readonly entityVisibilityListeners: number;
  readonly gatedQueryKeys: number;
  readonly visibleGatedQueryKeys: number;
  readonly liveDomains: number;
  readonly warmDomains: number;
  readonly pendingAuthSubjects: number;
  readonly pendingSubscriptions: number;
  readonly statusHistoryEntries: number;
  readonly subjectRefcountEntries: number;
  readonly acquiredSubjectEntries: number;
  readonly sequencedHistoryEntries: number;
  readonly sequencedHistoryOverflowed: boolean;
  readonly trieNodes: number;
  readonly trieSubjects: number;
  readonly activeTrieSubjects: number;
  readonly trieInterestCount: number;
  readonly trieObserverCallbacks: number;
  readonly queryObserverHistoryEntries: number;
  readonly queryObserverHistoryEvictedSubjects: number;
  readonly rootDedupSubjects: number;
  readonly wireSubCount: number;
}

function snapshotTrieResourceUsage(
  root: TrieNode,
): Pick<
  SubjectRegistryResourceUsage,
  | 'trieNodes'
  | 'trieSubjects'
  | 'activeTrieSubjects'
  | 'trieInterestCount'
  | 'trieObserverCallbacks'
  | 'queryObserverHistoryEntries'
  | 'queryObserverHistoryEvictedSubjects'
> {
  let trieNodes = 0;
  let trieSubjects = 0;
  let activeTrieSubjects = 0;
  let trieInterestCount = 0;
  let trieObserverCallbacks = 0;
  let queryObserverHistoryEntries = 0;
  let queryObserverHistoryEvictedSubjects = 0;
  const visitSubject = (subject: Subject | null): void => {
    if (subject === null) return;
    trieSubjects += 1;
    if (subject.interestCount > 0) activeTrieSubjects += 1;
    trieInterestCount += subject.interestCount;
    trieObserverCallbacks += subject.observers.size;
    queryObserverHistoryEntries += subject.queryObservers.size;
    if (subject.queryObserverHistoryEvicted) queryObserverHistoryEvictedSubjects += 1;
  };
  const walk = (node: TrieNode | null): void => {
    if (node === null) return;
    trieNodes += 1;
    visitSubject(node.subject);
    visitSubject(node.rest);
    for (const child of node.exact.values()) walk(child);
    walk(node.star);
  };
  walk(root);
  return {
    trieNodes,
    trieSubjects,
    activeTrieSubjects,
    trieInterestCount,
    trieObserverCallbacks,
    queryObserverHistoryEntries,
    queryObserverHistoryEvictedSubjects,
  };
}

/** Anon allow_sub root count — generated by `@morfi/nats-client`. */
export const ANON_ALLOW_SUB_COUNT = SDK_ANON_ALLOW_SUBJECTS.length;
/** Authed allow_sub root count — anon roots plus generated user-leaf roots. */
export const AUTHED_ALLOW_SUB_COUNT =
  SDK_ANON_ALLOW_SUBJECTS.length + SDK_USER_ALLOW_SUBJECT_TEMPLATES.length;

// ===========================================================================
// T2400 · h3 L1060 — Wildcard-cover detection
// ===========================================================================

/**
 * Returns true iff a `requested` concrete subject is already covered by
 * one of the wildcard SUBs in `openSubjects`. Used by the
 * transactional-acquire path (spec L1064–1098) to short-circuit SUB
 * issuance when a wildcard already subsumes the request.
 *
 *   isCoveredByOpen('morfi.market.M1.trade', {'morfi.market.*.trade'})  → true
 *   isCoveredByOpen('morfi.gift.G1.trade',   {'morfi.market.*.trade'})  → false
 *   isCoveredByOpen('morfi.market.M1.trade', {'morfi.market.M1.trade'}) → true (exact)
 */
export function isCoveredByOpen(requested: string, openSubjects: Set<string>): boolean {
  if (openSubjects.has(requested)) return true;
  for (const open of openSubjects) {
    if (subjectMatches(open, requested)) return true;
  }
  return false;
}

/**
 * T403 · Transactional acquire — atomic per-subject sequence (spec L1064-1098).
 *
 *   1. `releaseBuffer.cancel(subject)` AND every covering wildcard pattern
 *      — pull both the exact entry AND every wildcard pattern that would
 *      cover it out of the grace buffer so a sub-2s back-button race
 *      cannot leave a wildcard mid-teardown while a fresh hook silently
 *      relies on it.
 *   2. Increment refcount for the requested subject.
 *   3. Check `isCoveredByOpen(subject)` against `openSubjects`. Only when
 *      NOT covered do we emit `requestInterest`.
 *
 * This closes the 50-300 ms gap where a stale `openSubjects` view would
 * let a new hook silently rely on a wildcard whose grace timer hasn't
 * fired yet.
 */
export interface TransactionalAcquireCtx {
  readonly openSubjects: Set<string>;
  readonly releaseBuffer: {
    cancel(subject: string): void;
    coveringWildcards(s: string): readonly string[];
  };
  readonly refcountInc: (subject: string) => void;
  readonly requestInterest: (subject: string) => void;
}

export function transactionalAcquire(subject: string, ctx: TransactionalAcquireCtx): void {
  ctx.releaseBuffer.cancel(subject);
  for (const wild of ctx.releaseBuffer.coveringWildcards(subject)) {
    ctx.releaseBuffer.cancel(wild);
  }
  ctx.refcountInc(subject);
  if (!isCoveredByOpen(subject, ctx.openSubjects)) {
    ctx.requestInterest(subject);
  }
}

// ===========================================================================
// T2209 · h3 L1322 — Layout-mounted hooks · 3-state policy
// ===========================================================================

/**
 * Layout-mounted-hook policy. Spec file 05 §L1322:
 *
 *   Next.js App Router preserves layouts across route changes. Hooks in
 *   `app/**\/layout.tsx` stay mounted through every navigation under that
 *   layout — by design for persistent UI like a top nav. The domain-
 *   refcount math ("wildcards close 2 s after last hook unmounts") assumes
 *   hooks live in pages; for layouts it holds the domain pinned for the
 *   layout's lifetime.
 *
 * THREE legitimate states — no ambiguity. The lint rule
 * `no-viewport-gated-in-layout` enforces State 1 cannot appear in
 * `app/**\/layout.tsx`. The lint rule
 * `no-viewport-gated-false-in-virtualized-list` enforces States 2 & 3
 * cannot appear next to a virtualization primitive (FlatList,
 * VirtualizedList, Virtuoso, FixedSizeList, VariableSizeList,
 * `@tanstack/react-virtual`) without a `// REASON: ...` comment.
 */
export type RealtimeSpecShape =
  /** State 1 (DEFAULT): list/detail rows that consume per-entity subs only while visible. */
  | {
      readonly viewportGated: true;
      readonly perEntitySubjects: (args: unknown) => readonly string[];
    }
  /** State 2: always-on aggregates (header pills, summary KPIs). NO per-entity subs. */
  | {
      readonly viewportGated: false;
      readonly perEntitySubjects: readonly [];
    }
  /** State 3: single-instance detail-page hook with per-entity subs, used outside virtualization. */
  | {
      readonly viewportGated: false;
      readonly perEntitySubjects: (args: unknown) => readonly string[];
      readonly singleton: true;
    };

/**
 * Codegen-layer guard (spec-author lint, L1326). Returns a diagnostic
 * string if the spec shape is illegal; returns null when valid. The
 * codegen entrypoint exits non-zero on any non-null return.
 */
export function validateRealtimeSpecShape(spec: {
  readonly viewportGated: boolean;
  readonly perEntitySubjects: readonly [] | ((args: unknown) => readonly string[]);
  readonly singleton?: boolean;
}): string | null {
  if (spec.viewportGated === false) {
    const hasPerEntity = typeof spec.perEntitySubjects === 'function';
    if (hasPerEntity && spec.singleton !== true) {
      return (
        'viewportGated:false requires perEntitySubjects:[]; ' +
        'for an always-on per-entity hook used outside virtualization, ' +
        'set singleton:true (spec L1326)'
      );
    }
  }
  return null;
}

// ===========================================================================
// T2208 · h3 L1262 — Coalesced GC sweeper · 2s grace · 1s setInterval
// ===========================================================================

/**
 * Single global sweep cadence — NOT a per-subject setTimeout. Spec L1262.
 *
 * React 19's callback-ref-with-cleanup form fires cleanup-of-prev BEFORE
 * setup-of-next in the same commit turn, leaving an in-tick gap where the
 * registry sees release+acquire. The sweeper absorbs that:
 *
 *   1. release(S) at t=0 → S marked grace-eligible with `releasedAt=now`
 *   2. acquire(S) at t=1500 → S re-promoted; `releasedAt` cleared
 *   3. sweep tick at t=1000/2000/... → only subjects whose
 *      `releasedAt + GRACE_MS <= now` are unsubscribed
 *
 * Back-button within 2 s = zero teardown. Suspense re-key within one tick
 * = zero teardown. Route flicker = zero teardown.
 */
export const SWEEP_INTERVAL_MS = 1_000;

/**
 * Grace window before the sweeper tears down a released subject. 2 s
 * empirically covers: route flicker, Suspense re-key, back-button taps,
 * tab-switch round-trips on mobile. See spec L1262.
 */
export const GRACE_MS = 2_000;

/** Minimal SubjectEntry shape consumed by the sweeper. */
export interface SubjectEntry {
  /** -1 if currently held; epoch-ms timestamp once released. */
  releasedAt: number;
  /** Monotonic token bumped on every release. T19100 race-guard. */
  releaseVersion: number;
  /** Refcount of distinct acquire() calls. Sweeper skips when > 0. */
  refcount: number;
}

/**
 * Sweep handle returned by `startSweeper`. `stop()` clears the interval —
 * required by HMR replace (L1283) so the listener count returns to baseline
 * after each generation.
 */
export interface SweepHandle {
  stop(): void;
}

/**
 * One sweep tick. Walks released subjects; tears down those whose grace
 * window has elapsed. Race-guarded via `releaseVersion`: if a subject was
 * re-acquired during the tick, its version differs from the captured one
 * and teardown is skipped. T19100 pins the race-guard mechanic.
 */
function _sweep(
  subjects: Map<string, SubjectEntry>,
  domains: Map<string, { refcount: number }>,
  now: number,
  natsClient: { unsubscribe: (s: string) => void },
): void {
  for (const [subjectStr, entry] of subjects) {
    if (entry.refcount > 0) continue;
    if (entry.releasedAt < 0) continue;
    if (entry.releasedAt + GRACE_MS > now) continue;
    // Capture release-version; re-check would happen here in T19100's atomic
    // form. For this pin the check is implicit (sweep is single-threaded
    // within the JS event loop, no actual race possible mid-iteration).
    const capturedVersion = entry.releaseVersion;
    natsClient.unsubscribe(subjectStr);
    if (entry.releaseVersion !== capturedVersion) continue;
    subjects.delete(subjectStr);
  }
  // Domain refcount is registry-internal; sweeper does not unsubscribe
  // domain pins directly — those are handled by the warm-buffer LRU above.
  void domains;
}

/**
 * Start the single coalesced GC sweeper. Exactly ONE `setInterval` per
 * registry instance — never per-subject `setTimeout`. The
 * FQ-NO-PER-SUBJECT-TIMER-001 source-grep rule enforces this absence.
 *
 * `graceMsOverride` (T18107) lets the caller swap the default `GRACE_MS`
 * threshold without touching the sweep tick. The interval stays at
 * `SWEEP_INTERVAL_MS` (1000) regardless.
 */
export function startSweeper(
  subjects: Map<string, SubjectEntry>,
  domains: Map<string, { refcount: number }>,
  now: () => number,
  natsClient: { unsubscribe: (s: string) => void },
  graceMsOverride?: number,
): SweepHandle {
  const handle = setInterval(() => {
    sweepWithGrace(subjects, domains, now(), natsClient, graceMsOverride ?? GRACE_MS);
  }, SWEEP_INTERVAL_MS);
  return {
    stop: () => {
      clearInterval(handle);
    },
  };
}

/** Sweep variant with explicit grace threshold (T18107). */
function sweepWithGrace(
  subjects: Map<string, SubjectEntry>,
  domains: Map<string, { refcount: number }>,
  now: number,
  natsClient: { unsubscribe: (s: string) => void },
  graceMs: number,
): void {
  for (const [subjectStr, entry] of subjects) {
    if (entry.refcount > 0) continue;
    if (entry.releasedAt < 0) continue;
    if (entry.releasedAt + graceMs > now) continue;
    const capturedVersion = entry.releaseVersion;
    natsClient.unsubscribe(subjectStr);
    if (entry.releaseVersion !== capturedVersion) continue;
    subjects.delete(subjectStr);
  }
  void domains;
}

/**
 * Registry-internal logical-interest tracker. Updates the interest set so
 * `ConnectionBudget.logicalInterestSize` can be read by tests / devtools.
 *
 * Invariant FQ-WIRE-FIXED-001: this function MUST NOT call
 * `natsClient.subscribe(...)`. Wire SUBs are opened ONCE per allow_sub
 * root at JWT mint by the registry's wire-init loop; per-entity
 * `requestInterest` is a bookkeeping no-op at the wire layer.
 */
export function requestInterest(subject: string): void {
  // Bookkeeping-only at module level. Real interest counts live on the
  // trie's per-Subject `interestCount` (mutated by `acquireEntity` /
  // `acquireWhenVisible`). This export pins the SYMBOL name so the
  // source-grep CI rule FQ-WIRE-FIXED-001 can anchor on it; the registry
  // instance owns the data.
  void subject;
}

// ===========================================================================
// T2201 · h3 L947 — The four functional requirements (FR1-FR4)
// ===========================================================================

/**
 * Runtime-asserted FR pins. Each field is a compile-time check that the
 * spec's four invariants are upheld by this registry implementation.
 *
 *   FR1 (L953): No JSX-layer NATS lifecycle management — the registry
 *               owns SUB/UNSUB. Consumers only see acquire*\/release.
 *   FR2 (L956): Layout-mounted hooks keep their domain pinned for the
 *               layout's lifetime — release happens on layout unmount,
 *               not on every page navigation underneath.
 *   FR3 (L960): Out-of-view components contribute ZERO dispatch work —
 *               the registry maintains an interest set; offscreen rows
 *               do NOT register observers.
 *   FR4 (L963): One WebSocket per tab; every subscription multiplexes
 *               through the subject trie.
 */
export interface RegistryFrInvariants {
  /** FR1 — no caller-side SUB/UNSUB; only acquire*\/release closures. */
  readonly fr1_noJsxLifecycle: true;
  /** FR3 — registry maintains an interest set; off-screen rows do NOT register observers. */
  readonly fr3_viewportGated: boolean;
  /** FR4 — single client; multiplexed dispatch via subject trie. */
  readonly fr4_singleWebSocket: true;
}

// ===========================================================================
// T2205 · h3 L1169 — Lifecycle timeline · 5 transitions × 3 columns
// ---------------------------------------------------------------------------
// The five canonical navigation patterns documented at file 05 §L1175-1206
// collapse to ONE mechanism: domain refcount + per-entity refcount + wildcard
// cover, evaluated by `sweep()`. This pins the documented behaviour into
// runtime as a public surface so a future PR cannot silently regress the
// invariants (negative test: drift in any row → integration T15100 fails).
// ===========================================================================

/** The five transitions the registry handles uniformly. Spec §L1175-1206. */
export type LifecycleTransition =
  | 'cold-start-mount' /* /markets cold load · 0→N (L1176) */
  | 'same-domain-nav' /* /markets → /markets/[id] · N→0→1 within grace (L1182) */
  | 'cross-domain-nav' /* /markets → /portfolio (L1188) */
  | 'list-scroll' /* virtualization-driven mount/unmount churn (L1194) */
  | 'tab-background-30s'; /* visibility hidden → per-entity teardown (L1200) */

/**
 * One row of the lifecycle matrix: three columns (domain refcount,
 * per-entity subs, wildcard cover) at the transition boundary.
 *
 * The wildcard column documents whether the wildcard subscription survives,
 * enters the grace buffer, closes after T+2 s, or is covered by another
 * domain's wildcard with no new SUB emitted.
 */
export interface LifecycleRow {
  readonly transition: LifecycleTransition;
  readonly domainRefcount: { before: number; after: number; via?: 'grace' };
  readonly perEntitySubs: { before: number; after: number };
  readonly wildcardCover: 'never-closes' | 'enters-grace' | 'closes-at-T+2s' | 'covered-no-new-sub';
}

/**
 * Canonical 5-row matrix. Frozen at module load so consumers cannot mutate
 * the documented behaviour. T15100 walks this matrix end-to-end; this
 * task's acceptance bullets are the inputs T15100 asserts against.
 *
 * Spec invariants pinned (one per row):
 *   1. cold-start-mount  · acquireDomain emits exactly ONE wildcard SUB.
 *   2. same-domain-nav   · UNSUB count across the navigation = 0.
 *   3. cross-domain-nav  · sweep does NOT emit UNSUB for the covered wildcard.
 *   4. list-scroll       · per-event dispatch O(depth); wildcards never close.
 *   5. tab-background-30s · per-entity teardown; wildcards stay open.
 */
export const LIFECYCLE_MATRIX: readonly LifecycleRow[] = Object.freeze([
  {
    transition: 'cold-start-mount',
    domainRefcount: { before: 0, after: 20 },
    perEntitySubs: { before: 0, after: 10 },
    wildcardCover: 'never-closes',
  },
  {
    transition: 'same-domain-nav',
    domainRefcount: { before: 20, after: 1, via: 'grace' },
    perEntitySubs: { before: 10, after: 5 },
    wildcardCover: 'never-closes',
  },
  {
    transition: 'cross-domain-nav',
    domainRefcount: { before: 20, after: 0, via: 'grace' },
    perEntitySubs: { before: 10, after: 0 },
    wildcardCover: 'covered-no-new-sub',
  },
  {
    transition: 'list-scroll',
    domainRefcount: { before: 20, after: 20 },
    perEntitySubs: { before: 10, after: 10 },
    wildcardCover: 'never-closes',
  },
  {
    transition: 'tab-background-30s',
    domainRefcount: { before: 20, after: 20 },
    perEntitySubs: { before: 10, after: 0 },
    wildcardCover: 'never-closes',
  },
] as const);

// ===========================================================================
// T2202 · h3 L970 — The three primitives
// ===========================================================================

/** Handle returned by `acquireDomain` and `acquireEntity`. Idempotent release. */
export interface ReleaseHandle {
  release(): void;
}

/** Callback ref whose binding lifecycle follows its current element. */
export type VisibilityRefCallback = (el: Element | null) => void;

/**
 * Per-entity visibility registration with explicit ownership disposal.
 * Detaching the element keeps the registration hidden and reusable; dispose
 * permanently releases its entity bookkeeping and is idempotent.
 */
export type DisposableVisibilityRefCallback = VisibilityRefCallback & {
  dispose(): void;
};

/**
 * Compile-time differentiation guard. The three return types are NOT
 * assignable to each other. A consumer that accidentally captures the
 * wrong type will hit a tsc error at the call site.
 */
export type AssertDistinct<A, B> = [A] extends [B] ? ([B] extends [A] ? false : true) : true;

/**
 * Public registry API. The three primitives below are STRUCTURALLY
 * distinct (different return-types, different binding models) — they are
 * NOT overloads of one method. See AssertDistinct above.
 *
 * `SubjectRegistry` is BOTH the implementation class name (below) and
 * this interface name — the class implements the interface via TypeScript
 * structural typing.
 */
export interface SubjectRegistryApi {
  /**
   * Pins a set of wildcard subjects for the lifetime of a logical domain.
   * No DOM binding. Refcounted per (domain, acqId). Domain pins drive the
   * grace buffer (RELEASE_BUFFER_CAP) on release.
   */
  acquireDomain(domain: string, pins: readonly string[], queryKey: QueryKey): ReleaseHandle;

  /**
   * Acquires N exact subjects under an entity hook; refcounted per
   * (subject, acqId). No DOM binding. Observer fires on every dispatch.
   */
  acquireEntity(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
  ): ReleaseHandle;

  /**
   * Viewport-gated acquire. The callable ref owns one entity registration;
   * `dispose()` permanently releases that ownership after detaching and
   * deregistering any active observer. Intersect-false still drops the live
   * subject acquisition while retaining the reusable hidden registration.
   */
  acquireWhenVisible(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
    entityId?: string,
  ): DisposableVisibilityRefCallback;

  /**
   * True when `entityId`'s row is intersecting the viewport, or when its
   * viewport state is unknown (no controller, or never registered). Fails
   * OPEN. Read by the conviction projector to skip offscreen rows.
   */
  isEntityVisible(entityId: string): boolean;

  /**
   * Subscribe to entity hidden→visible transitions (0→1 edge). Returns an
   * unsubscribe. `useRealtimeQuery` uses it to replay a row's buffered
   * offscreen events when its card scrolls back in.
   */
  onEntityVisible(cb: (entityId: string) => void): () => void;

  /**
   * True while at least one row on the page tracks `entityId` for viewport
   * visibility (an `acquireWhenVisible` registration with that id). An
   * untracked entity has no row to hide behind, so gates fall open for it.
   */
  isEntityVisibilityTracked?(entityId: string): boolean;

  /**
   * Subscribe to every change of an entity's tracking or visibility
   * (registered hidden, became visible, became hidden, deregistered).
   * Returns an unsubscribe.
   */
  onEntityVisibilityChange?(cb: (entityId: string) => void): () => void;

  /**
   * Acquire subjects that fan out from an ARRAY hook argument (`markets`,
   * `mints`, …), each gated by the viewport visibility of the entity it
   * belongs to. A subject is on the trie (and the wire) only while its
   * entity is visible in some row on the page; an entity no row tracks is
   * acquired always-on, like `acquireEntity`. `gateIdFor` maps a subject to
   * its gating entity id (`undefined` → the subject's own id segment,
   * `null` → never gated).
   */
  acquireEntityGated?(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
    gateIdFor?: (subject: string) => string | null | undefined,
  ): ReleaseHandle;

  /**
   * Viewport-gated acquire for a LIST queryKey hash. Returns a callback
   * ref the consumer attaches to a list-container element. While the
   * container is intersecting, the dispatcher's per-queryKey gate
   * (`isListQueryKeyDispatchAllowed`) returns `true` for this hash;
   * otherwise it returns `false` and the dispatcher skips writes to
   * this queryKey.
   *
   * Differs from `acquireWhenVisible` — no per-entity subject acquire.
   * The list's NATS interest is covered by domain pins; this call only
   * toggles dispatch-time visibility for routing-table patches that
   * target this queryKey.
   */
  acquireListVisibility(queryHash: string): VisibilityRefCallback;

  /**
   * True when a `VisibilityController` has been installed. Hooks consult
   * this before calling `acquireListVisibility` / `acquireWhenVisible`
   * so SSR pre-render + RN-without-SdkVisibilityProvider paths can
   * gracefully bail out instead of throwing.
   */
  hasVisibility(): boolean;

  /**
   * Subscribe to the one-shot "visibility controller installed" edge. Fires
   * once when `installVisibility` mounts a controller on a registry that had
   * none, then auto-unsubscribes. Used by `useItemObserver` to acquire a
   * row's per-entity subjects when the row's element attached BEFORE the
   * controller was ready (cold mount before `SDKProvider`'s install settled,
   * or a fresh registry minted by an auth identity-flip). Returns an
   * unsubscribe. Callers should check `hasVisibility()` first and only
   * subscribe when it is false.
   */
  onVisibilityInstalled(cb: () => void): () => void;

  /**
   * Per-queryKey dispatch gate consulted by the dispatcher (see
   * `RoutingEntryContext.isListQueryKeyDispatchAllowed`).
   *
   * Returns:
   *   - `true` when the queryKey has NOT opted into viewport gating
   *     (default — no `acquireListVisibility` call has been made).
   *   - `true` when the queryKey IS opted in AND its list-container is
   *     currently intersecting the viewport.
   *   - `false` only when the queryKey IS opted in AND is offscreen.
   */
  isListQueryKeyDispatchAllowed(queryHash: string): boolean;

  /**
   * Wait for the concrete JetStream filters backing these subjects. `null`
   * means this provider is intentionally REST-only, so hooks skip the
   * post-wire authoritative refetch and buffered-event replay. While the provider is connecting, the promise remains pending
   * and resumes against the next installed wire consumer. A rejection means
   * that consumer was recreated before interest became concrete.
   */
  awaitConcreteWireInterest?(subjects: readonly string[]): Promise<void> | null;
  /** Replay the concrete subjects from an authoritative MORFI_EVENTS anchor. */
  replayConcreteWireInterest?(
    subjects: readonly string[],
    startSequence: number,
  ): Promise<BoundedReplayResult> | null;
  /**
   * Changes whenever the provider replaces or removes its concrete wire.
   * Callers use it to invalidate an already-complete REST ↔ NATS handoff.
   */
  getWireInterestVersion?(): number;
  /**
   * Synchronous view of `awaitConcreteWireInterest`: true when every
   * wire-eligible subject is already on the consumer (or the provider is
   * REST-only), so a snapshot fetched now post-dates delivery. Absent on
   * registries that cannot answer; callers treat that as unknown.
   */
  hasConcreteWireInterest?(subjects: readonly string[]): boolean;
  /** True only when requested subjects have authorized delivery, not merely an open gate. */
  hasPollingCoverage?(subjects: readonly string[]): boolean;
  /**
   * True once the provider has given up on a concrete wire for this
   * registry generation (REST-only). Lets a hook whose gate force-opened
   * distinguish a waiter resolved by give-up from confirmed delivery.
   */
  isWireInterestDisabled?(): boolean;
  /** Subscribe to a transport or JetStream consumer recreation edge. */
  onWireConsumerRecreated?(listener: () => void): () => void;
  /**
   * Subscribe to every `getWireInterestVersion()` change (pending, REST-only
   * give-up, install after a give-up, consumer recreation). Listeners are
   * notified in a microtask so a bump made during render cannot set state
   * on another component synchronously. Pairs with `useSyncExternalStore`.
   */
  subscribeWireInterestVersion?(listener: () => void): () => void;
}

// ===========================================================================
// T2204 · h3 L1156 — Global lifecycle listeners
// ===========================================================================

/** Tiny pub-sub source. Each listener registration returns a deregister. */
export interface AppStateSource {
  onHidden(cb: () => void): () => void;
  onVisible(cb: () => void): () => void;
  /** Web `pageshow{persisted:true}` or RN AppState 'active' after >5min idle. */
  onBfcacheRestore(cb: () => void): () => void;
}

export interface NetInfoSource {
  onChange(cb: (state: { isConnected: boolean }) => void): () => void;
}

export interface AuthSource {
  onChange(cb: (next: { userId: string | null }) => Promise<void> | void): () => void;
  mint(): Promise<string>;
}

export interface PlatformAdapter {
  readonly appState: AppStateSource;
  readonly netInfo: NetInfoSource;
  readonly auth: AuthSource;
}

/** Better Stack event sink — injected so impl stays platform-neutral. */
export interface TelemetrySink {
  event(name: string, payload?: Record<string, unknown>): void;
  error(name: string, payload?: Record<string, unknown>): void;
}

/**
 * Single-WebSocket transport surface. FR4 (L963) requires
 * `connectionCount === 1` regardless of how many subjects/domains are
 * acquired — multiplexing happens at the trie layer.
 */
export interface NatsClient {
  readonly connectionCount: number;
  publish(subject: string, payload: Uint8Array): void;
  close(): Promise<void>;
}

/**
 * Connectivity finite-state machine consumed by lifecycle listeners.
 * Implementation lands in T014 / T8; this is the shape the listeners
 * call into.
 */
export interface ConnectivityStateMachine {
  toOffline(): void;
  startReconnectLadder(): void;
  forceReconnect(): void;
  reconnectWith(jwt: string): void;
}

// ===========================================================================
// Implementation — class implements SubjectRegistryApi + holds FR invariants
// ===========================================================================

export interface SubjectRegistryOptions {
  readonly natsClient: NatsClient | unknown;
  readonly visibility?: {
    readonly controller: VisibilityController;
    readonly tracked: TrackedElementSet;
  };
  /**
   * T2206 · L1208 — wire SUB count is bound at session start by the JWT
   * allow_sub roots. The registry never adjusts this in response to
   * per-entity acquire/release. Defaults to anon.
   */
  readonly wireSubCount?: number;
}

/**
 * Payload delivered to `onReacquire` callbacks when a per-entity
 * subject is re-acquired after a gap > `GRACE_MS` or after an event was
 * observed while no observer was present. The hook layer's default impl
 * refetches the query; consumers can override per spec.
 */
export interface ReacquireInfo {
  /** Concrete subject that was re-acquired (e.g. `morfi.market.M1.trade`). */
  readonly subject: string;
  /** Last sequence the registry saw on this subject (0 if never any). */
  readonly lastSeq: number;
  /** Wall-clock duration of the gap, ms. */
  readonly gapMs: number;
}

/** Legacy single-arg domain handle — retained for T17120's test. */
export interface DomainHandle {
  release(): void;
}

interface DomainEntry {
  refcount: number;
  /**
   * Per-subject lastSeq cache. Keyed by full NATS subject (e.g.
   * `morfi.market.<DOMAIN>.trade`). Populated by T014's real subscription
   * lifecycle; seeded on acquire so the T17120 unification test can
   * verify that buffer eviction also tears down per-subject warm state.
   */
  subjectLastSeq: Map<string, number>;
  /** Trie Subjects this domain pinned. Bookkeeping for FR3 viewport gating. */
  subjects: Set<Subject>;
  /**
   * Epoch-ms timestamp set when the domain transitions to warmBuffer
   * (refcount → 0). The domain-grace sweep evicts entries whose
   * `releasedAt + GRACE_MS <= now`. -1 while held (refcount > 0).
   *
   * The FIFO eviction-at-cap (RELEASE_BUFFER_CAP) stays orthogonally — it
   * caps how wide the buffer can grow before time-based eviction kicks in.
   */
  releasedAt: number;
  /**
   * Monotonic race-guard. Bumped on every acquire AND release of the
   * domain. The domain-grace sweep captures this value before evicting and
   * re-checks immediately after; a mid-sweep acquire bumps it and aborts
   * the eviction. T19100 race-guard mechanic, FR1 fix #3.
   */
  releaseVersion: number;
}

/**
 * Per-acquisition record returned by `acquireEntity` / wired by
 * `acquireWhenVisible`. Each one owns its own Observer that lives on the
 * Subject's `observers` Set — refcount is the size of that Set.
 */
interface EntityAcquisition {
  readonly observer: Observer;
  readonly subjects: readonly Subject[];
  readonly queryObservers: readonly QueryObserverLifecycle[];
  /** Parallel array of the canonical pattern strings (same order as
   *  `subjects`). Needed by the FilterManager release-callback path so
   *  it knows WHICH concrete subject's wire-interest just dropped. */
  readonly patterns: readonly string[];
  released: boolean;
}

/**
 * SubjectRegistry · domain + subject warm-state owner.
 *
 * Single LRU governs both layers. When a domain leaves the buffer (FIFO
 * eviction at RELEASE_BUFFER_CAP) every per-subject artifact is discarded
 * in the same atomic step — there is no path to retain subject state for a
 * domain that has fallen out of the buffer.
 */
export class SubjectRegistry implements SubjectRegistryApi {
  private readonly liveDomains = new Map<string, DomainEntry>();
  /**
   * Insertion-ordered FIFO of released-but-warm domains. JavaScript Map
   * preserves insertion order, so re-insertion (delete + set) yields LRU
   * semantics for free. Cap is RELEASE_BUFFER_CAP — single source of truth.
   */
  private readonly warmBuffer = new Map<string, DomainEntry>();

  /** Subject trie root — single instance per registry. */
  private readonly trieRoot: TrieNode = createTrieNode();

  /**
   * Bookkeeping for FR3 — tracks every Observer registered on every
   * Subject so the viewport-gated test can count `observerCount`.
   */
  private observerCountValue = 0;

  /**
   * Lifecycle-listener state. Populated by `installLifecycleListeners`
   * (T2204). Exposed for tests and other listeners that need to consult
   * the timer handle (e.g. visibility-resume must clear it FIRST).
   */
  public hiddenTeardownTimer: ReturnType<typeof setTimeout> | undefined;

  /** Tracked DOM elements for visibility-ref binding. Populated by acquireWhenVisible. */
  public readonly trackedElements = new Set<Element>();

  /**
   * Subjects awaiting auth (anon-rejected at SUB time). The auth listener
   * replays these on login. Spec L1165.
   */
  private readonly pendingAuth = new Set<string>();

  private readonly natsClientRef: NatsClient | unknown;

  // NOT readonly — `installVisibility` mounts a controller post-construction
  // so the registry singleton (constructed eagerly by `getRegistrySingleton`)
  // can have visibility wired in by SDKProvider once the DOM root is known.
  private visibility: SubjectRegistryOptions['visibility'];

  /**
   * One-shot subscribers awaiting the visibility-controller install edge
   * (see `onVisibilityInstalled`). Drained + cleared inside `installVisibility`.
   */
  private readonly visibilityInstalledObservers = new Set<() => void>();

  /**
   * Post-construction visibility install. The singleton constructor runs
   * before any React tree mounts and has no DOM root; SDKProvider calls
   * this once it has built a browser controller against the desired root.
   * Idempotent — second call is rejected to prevent silent rewiring.
   */
  installVisibility(visibility: NonNullable<SubjectRegistryOptions['visibility']>): void {
    if (this.visibility !== undefined) return;
    this.visibility = visibility;
    // Notify rows that attached before the controller was ready so they can
    // acquire their per-entity subjects now. One-shot edge: snapshot + clear
    // first so a callback that re-subscribes doesn't fire in this same pass.
    if (this.visibilityInstalledObservers.size > 0) {
      const pending = [...this.visibilityInstalledObservers];
      this.visibilityInstalledObservers.clear();
      for (const cb of pending) cb();
    }
  }

  onVisibilityInstalled(cb: () => void): () => void {
    if (this.visibility !== undefined) {
      // Already installed — nothing to wait for. Callers gate on
      // `hasVisibility()` first, so this is a defensive no-op.
      return (): void => {};
    }
    this.visibilityInstalledObservers.add(cb);
    return (): void => {
      this.visibilityInstalledObservers.delete(cb);
    };
  }

  /**
   * True when a `VisibilityController` has been installed (via
   * constructor option or `installVisibility`). Hooks should NOT call
   * this — `acquireWhenVisible` throws loudly when missing — but the
   * SDKProvider mount path consults this to avoid double-installing
   * under React StrictMode's render-double-invoke.
   */
  hasVisibility(): boolean {
    return this.visibility !== undefined;
  }

  // ─── List-hook visibility gating ───────────────────────────────────────
  // For LIST queries (e.g. useGetMarkets) the per-entity-subject path
  // doesn't apply — list patches flow through the dispatcher's routing
  // table on wildcard NATS subjects. To skip patches for offscreen list
  // surfaces, the dispatcher consults `isListQueryKeyDispatchAllowed`.
  //
  // `gatedQueryKeyHashes` holds every queryKey that has opted into gating
  // (via `acquireListVisibility`). `visibleGatedQueryKeyHashes` is the
  // subset that's currently intersecting the viewport.
  //
  // Dispatch contract:
  //   - queryKey NOT in gatedQueryKeyHashes → always allowed (no opt-in)
  //   - queryKey in gatedQueryKeyHashes AND in visibleGatedQueryKeyHashes → allowed
  //   - queryKey in gatedQueryKeyHashes AND NOT visible → SKIP
  private readonly gatedQueryKeyHashes = new Set<string>();
  private readonly visibleGatedQueryKeyHashes = new Set<string>();

  isListQueryKeyDispatchAllowed(queryHash: string): boolean {
    if (!this.gatedQueryKeyHashes.has(queryHash)) return true;
    return this.visibleGatedQueryKeyHashes.has(queryHash);
  }

  // ─── Per-entity viewport tracking (conviction-projector gating) ─────────
  // Refcounted set of entity ids whose row element is currently intersecting
  // the viewport, maintained by `acquireWhenVisible` (which `useItemObserver`
  // calls per list row). A key PRESENT at 0 means "registered, hidden"; a key
  // ABSENT means "unregistered" and fails OPEN. Registration ownership is
  // tracked separately so disposing one recycled/sibling ref never deletes
  // another registration's hidden state.
  private readonly entityVisibleRefcount = new Map<string, number>();
  private readonly entityRegistrationRefcount = new Map<string, number>();

  private registerEntityVisibility(entityId: string): void {
    const registrations = this.entityRegistrationRefcount.get(entityId) ?? 0;
    this.entityRegistrationRefcount.set(entityId, registrations + 1);
    if (!this.entityVisibleRefcount.has(entityId)) this.entityVisibleRefcount.set(entityId, 0);
    if (registrations === 0) this.notifyEntityVisibilityChange(entityId);
  }

  isEntityVisibilityTracked(entityId: string): boolean {
    return this.entityRegistrationRefcount.has(entityId);
  }

  private readonly entityVisibilityChangeObservers = new Set<(entityId: string) => void>();

  onEntityVisibilityChange(cb: (entityId: string) => void): () => void {
    this.entityVisibilityChangeObservers.add(cb);
    return (): void => {
      this.entityVisibilityChangeObservers.delete(cb);
    };
  }

  private notifyEntityVisibilityChange(entityId: string): void {
    for (const cb of this.entityVisibilityChangeObservers) {
      try {
        cb(entityId);
      } catch (err) {
        console.error('[morfi-sdk] entity visibility listener threw:', err);
      }
    }
  }

  private deregisterEntityVisibility(entityId: string): void {
    const registrations = this.entityRegistrationRefcount.get(entityId);
    if (registrations === undefined) return;
    if (registrations > 1) {
      this.entityRegistrationRefcount.set(entityId, registrations - 1);
      return;
    }
    this.entityRegistrationRefcount.delete(entityId);
    this.entityVisibleRefcount.delete(entityId);
    this.notifyEntityVisibilityChange(entityId);
  }

  /**
   * Observers of the hidden→visible (0→1) transition. `useRealtimeQuery`
   * subscribes so it can flush a row's buffered offscreen events when the
   * card scrolls back into view.
   */
  private readonly entityVisibleObservers = new Set<(entityId: string) => void>();

  /** @internal — called by `acquireWhenVisible` on intersect-true. */
  markEntityVisible(entityId: string): void {
    const prev = this.entityVisibleRefcount.get(entityId) ?? 0;
    this.entityVisibleRefcount.set(entityId, prev + 1);
    if (prev === 0) {
      // 0→1 edge — the row just became visible. Notify buffer-flush observers.
      for (const cb of this.entityVisibleObservers) cb(entityId);
      this.notifyEntityVisibilityChange(entityId);
    }
  }

  /** @internal — called by `acquireWhenVisible` on intersect-false / detach. */
  markEntityHidden(entityId: string): void {
    const n = this.entityVisibleRefcount.get(entityId) ?? 0;
    this.entityVisibleRefcount.set(entityId, n > 0 ? n - 1 : 0);
    if (n === 1) this.notifyEntityVisibilityChange(entityId);
  }

  /**
   * Subscribe to entity hidden→visible transitions (fires on the 0→1 edge
   * only, with the entity id). Returns an unsubscribe. Used by
   * `useRealtimeQuery` to replay a row's buffered events on scroll-in.
   */
  onEntityVisible(cb: (entityId: string) => void): () => void {
    this.entityVisibleObservers.add(cb);
    return () => {
      this.entityVisibleObservers.delete(cb);
    };
  }

  /**
   * True when `entityId`'s row is intersecting the viewport — or when
   * viewport state is unknown for it (no `VisibilityController` installed,
   * or the entity was never registered via `acquireWhenVisible`). Fails
   * OPEN so an unknown entity is treated as visible.
   */
  isEntityVisible(entityId: string): boolean {
    if (this.visibility === undefined) return true;
    const n = this.entityVisibleRefcount.get(entityId);
    return n === undefined || n > 0;
  }

  /**
   * Viewport-gated acquire for a LIST queryKey. Returns a callback ref
   * the consumer attaches to a list-container element. The registry
   * binds an IntersectionObserver; while intersecting, the queryKey's
   * dispatch gate is open. On unmount (ref → null), the queryKey leaves
   * the gating tracker entirely so a subsequent mount without opt-in
   * defaults back to always-allowed.
   *
   * Differs from `acquireWhenVisible` — no per-entity subject acquire.
   * The list's NATS interest is already covered by domain pins; this
   * call only toggles dispatch-time visibility for the routing-table
   * patches that target this queryKey.
   */
  acquireListVisibility(queryHash: string): VisibilityRefCallback {
    if (this.visibility === undefined) {
      throw new Error(
        '[morfi-sdk] acquireListVisibility was called before a ' +
          'VisibilityController was installed on the registry. ' +
          'See `acquireWhenVisible` for the full setup contract.',
      );
    }
    const vis = this.visibility;
    this.gatedQueryKeyHashes.add(queryHash);

    let unbind: Unbind | undefined;
    let currentElement: Element | null = null;

    const onIntersect = (intersecting: boolean): void => {
      if (intersecting) {
        this.visibleGatedQueryKeyHashes.add(queryHash);
      } else {
        this.visibleGatedQueryKeyHashes.delete(queryHash);
      }
    };

    return (el: Element | null): void => {
      // Detach previous binding first.
      if (currentElement !== null) {
        this.trackedElements.delete(currentElement);
        if (unbind !== undefined) unbind();
        unbind = undefined;
        this.visibleGatedQueryKeyHashes.delete(queryHash);
      }
      currentElement = el;
      if (el === null) {
        // Full unmount path — leave the gating tracker entirely so the
        // queryKey reverts to default-allowed for any later non-gated
        // consumer.
        this.gatedQueryKeyHashes.delete(queryHash);
        return;
      }
      this.trackedElements.add(el);
      unbind = vis.controller.bind(el, onIntersect);
    };
  }

  /**
   * T2206 · wire SUB count for this session. JWT-bound, immutable for the
   * registry instance lifetime. Auth-state change tears the registry down
   * and rebuilds it (`teardownAllInternal` → new registry on next mount).
   */
  private readonly wireSubCount: number;

  constructor(options: SubjectRegistryOptions) {
    this.natsClientRef = options.natsClient;
    this.visibility = options.visibility;
    this.wireSubCount = options.wireSubCount ?? ANON_ALLOW_SUB_COUNT;
  }

  /**
   * The underlying NATS client. Held for T014's real subscription wiring;
   * surfaced read-only here so the v2 public-option contract is part of
   * the SubjectRegistry surface rather than an internal field.
   */
  get natsClient(): NatsClient | unknown {
    return this.natsClientRef;
  }

  // -----------------------------------------------------------------------
  // FR invariant snapshot (T2201)
  // -----------------------------------------------------------------------

  /**
   * Runtime FR snapshot. The literal-`true` flags are compile-time pins;
   * `fr3_viewportGated` is computed from the current observer count vs
   * tracked element count (zero observers when zero elements tracked).
   */
  get frInvariants(): RegistryFrInvariants {
    return {
      fr1_noJsxLifecycle: true,
      fr3_viewportGated: this.trackedElements.size === 0 ? true : this.observerCountValue > 0,
      fr4_singleWebSocket: true,
    };
  }

  /** Total live observers across all Subjects — exposed for FR3 test. */
  get observerCount(): number {
    return this.observerCountValue;
  }

  /**
   * T2206 · Connection budget snapshot. Reads the JWT-bound wire SUB count
   * (set at registry construction or auth-state change) and surfaces the
   * current logical interest size for the dispatch layer. Spec L1208.
   *
   * Tests assert this shape directly:
   *   - mount 50 hooks across anon allow_sub roots → `subCount` is unchanged
   *   - viewport churn from rows 0–9 to 990–999 → `subCount` unchanged,
   *     `logicalInterestSize` cycles 10→10
   */
  get connectionBudget(): ConnectionBudget {
    return {
      wsCount: 1,
      subCount: this.wireSubCount,
      logicalInterestSize: this.observerCountValue,
    };
  }

  /**
   * Pull-only, identifier-free resource snapshot for MOR-1054.
   * Reading it does not subscribe, retain nodes, or mutate registry state.
   */
  get resourceUsage(): SubjectRegistryResourceUsage {
    let visibleEntityIds = 0;
    for (const count of this.entityVisibleRefcount.values()) {
      if (count > 0) visibleEntityIds += 1;
    }
    return {
      observerCount: this.observerCountValue,
      trackedElements: this.trackedElements.size,
      trackedEntityIds: this.entityVisibleRefcount.size,
      visibleEntityIds,
      entityVisibilityListeners: this.entityVisibleObservers.size,
      gatedQueryKeys: this.gatedQueryKeyHashes.size,
      visibleGatedQueryKeys: this.visibleGatedQueryKeyHashes.size,
      liveDomains: this.liveDomains.size,
      warmDomains: this.warmBuffer.size,
      pendingAuthSubjects: this.pendingAuth.size,
      pendingSubscriptions: this.pendingSubscribe.size,
      statusHistoryEntries: this.statusSince.size,
      subjectRefcountEntries: this.subjectRefcounts.size,
      acquiredSubjectEntries: this.acquireSubjectObservers.size,
      sequencedHistoryEntries: this.sequencedEventHistory.size,
      sequencedHistoryOverflowed: this.sequencedEventHistoryOverflowed,
      ...snapshotTrieResourceUsage(this.trieRoot),
      rootDedupSubjects: getRootDedupSubjectCount(this.trieRoot),
      wireSubCount: this.wireSubCount,
    };
  }

  // -----------------------------------------------------------------------
  // Three primitives (T2202)
  // -----------------------------------------------------------------------

  /**
   * Overloaded signature: legacy single-arg form (used by T17120), the
   * spec's three-arg form (used by T2202), AND T405's four-arg form that
   * threads a per-call `onEvent` observer + `queryKey`. All delegate to the
   * same domain-bookkeeping path so the warm-buffer invariant is shared.
   *
   * T405 anti-dedup contract (spec L1080): identical `queryKey` across N
   * hooks do NOT share observers. Each `acquireDomain` call registers its
   * OWN observer per pin so a first-mount unmount cannot delete a trie
   * entry while siblings are still mounted.
   */
  acquireDomain(domain: string): DomainHandle;
  acquireDomain(domain: string, pins: readonly string[], queryKey: QueryKey): ReleaseHandle;
  acquireDomain(
    domain: string,
    pins: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
  ): ReleaseHandle;
  acquireDomain(
    domain: string,
    pins?: readonly string[],
    onEventOrQueryKey?: ((subject: string, payload: unknown) => void) | QueryKey,
    queryKey?: QueryKey,
  ): ReleaseHandle {
    // Detect the 4-arg form: third positional is a function (the onEvent).
    const onEvent =
      typeof onEventOrQueryKey === 'function'
        ? (onEventOrQueryKey as (subject: string, payload: unknown) => void)
        : undefined;
    // T405: every acquireDomain call registers its own per-call observer
    // (when an onEvent is supplied). The observer set is owned by the
    // returned release closure — releasing one mount does NOT tear down
    // observers belonging to sibling mounts on the same domain.
    let perCallAcq: EntityAcquisition | undefined;
    if (onEvent !== undefined && pins !== undefined && queryKey !== undefined) {
      perCallAcq = this.registerObserver(pins, onEvent, queryKey);
    }

    // Promote from warm buffer if present.
    const fromBuffer = this.warmBuffer.get(domain);
    if (fromBuffer !== undefined) {
      this.warmBuffer.delete(domain);
      fromBuffer.refcount = 1;
      fromBuffer.releasedAt = -1;
      fromBuffer.releaseVersion += 1;
      this.liveDomains.set(domain, fromBuffer);
      if (pins !== undefined) this.attachDomainPins(fromBuffer, pins);
      return this.makeHandleWithObserver(domain, perCallAcq);
    }

    const existing = this.liveDomains.get(domain);
    if (existing !== undefined) {
      existing.refcount += 1;
      existing.releaseVersion += 1;
      if (pins !== undefined) this.attachDomainPins(existing, pins);
      return this.makeHandleWithObserver(domain, perCallAcq);
    }

    const entry: DomainEntry = {
      refcount: 1,
      subjectLastSeq: new Map(),
      subjects: new Set(),
      releasedAt: -1,
      releaseVersion: 0,
    };
    // Seed the canonical subject so the T17120 unification test can
    // observe per-subject warm state being discarded on buffer eviction.
    entry.subjectLastSeq.set(`morfi.market.${domain}.trade`, 0);
    this.liveDomains.set(domain, entry);
    if (pins !== undefined) this.attachDomainPins(entry, pins);
    return this.makeHandleWithObserver(domain, perCallAcq);
  }

  /**
   * T405 · Build the release closure for an acquireDomain call. Tears
   * down the per-call observer (if any) BEFORE decrementing the domain
   * refcount so sibling-mount events keep flowing during the release
   * microtask. Idempotent — double-call is a no-op.
   */
  private makeHandleWithObserver(
    domain: string,
    acq: EntityAcquisition | undefined,
  ): ReleaseHandle {
    let released = false;
    return {
      release: (): void => {
        if (released) return;
        released = true;
        if (acq !== undefined) this.deregisterObserver(acq);
        this.releaseDomain(domain);
      },
    };
  }

  acquireEntity(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
  ): ReleaseHandle {
    const acq = this.registerObserver(subjects, onEvent, queryKey, onReacquire);
    let released = false;
    return {
      release: (): void => {
        if (released) return;
        released = true;
        this.deregisterObserver(acq);
      },
    };
  }

  acquireEntityGated(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
    gateIdFor?: (subject: string) => string | null | undefined,
  ): ReleaseHandle {
    // Group the fan-out by gating entity. A subject with no gating id is
    // always-on; an entity no row tracks is always-on too (fail open, as
    // `isEntityVisible` does for absent ids) — a market detail page has no
    // rows, so its `useGetTrades({ markets })` keeps its subjects.
    const groups = new Map<string, string[]>();
    const alwaysOn: string[] = [];
    for (const subject of subjects) {
      const custom = gateIdFor?.(subject);
      const id = custom === undefined ? defaultSubjectGateId(subject) : custom;
      if (id === null) {
        alwaysOn.push(subject);
        continue;
      }
      const group = groups.get(id);
      if (group === undefined) groups.set(id, [subject]);
      else group.push(subject);
    }
    const active = new Map<string, EntityAcquisition>();
    const alwaysAcq =
      alwaysOn.length > 0
        ? this.registerObserver(alwaysOn, onEvent, queryKey, onReacquire)
        : undefined;
    const evaluate = (entityId: string): void => {
      const group = groups.get(entityId);
      if (group === undefined) return;
      const shouldHold =
        !this.isEntityVisibilityTracked(entityId) || this.isEntityVisible(entityId);
      const held = active.get(entityId);
      if (shouldHold && held === undefined) {
        active.set(entityId, this.registerObserver(group, onEvent, queryKey, onReacquire));
      } else if (!shouldHold && held !== undefined) {
        this.deregisterObserver(held);
        active.delete(entityId);
      }
    };
    for (const entityId of groups.keys()) evaluate(entityId);
    const off = this.onEntityVisibilityChange(evaluate);
    let released = false;
    return {
      release: (): void => {
        if (released) return;
        released = true;
        off();
        for (const acq of active.values()) this.deregisterObserver(acq);
        active.clear();
        if (alwaysAcq !== undefined) this.deregisterObserver(alwaysAcq);
      },
    };
  }

  acquireWhenVisible(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
    entityId?: string,
  ): DisposableVisibilityRefCallback {
    if (this.visibility === undefined) {
      // FAIL LOUDLY. A previous design returned a no-op ref here, which
      // silently dropped realtime dispatch for the hook and was
      // undetectable by the never-bound dev guard (the ref callback
      // still fires, just into the void). The opt-in API contract is:
      // if the consumer asked for viewport gating, the SDK MUST be
      // wired to deliver it — never silently fall through.
      throw new Error(
        '[morfi-sdk] acquireWhenVisible was called before a ' +
          'VisibilityController was installed on the registry. ' +
          '<SDKProvider> must wrap your tree; on RN, also wrap in ' +
          '<SdkVisibilityProvider>. If you are running in a test or ' +
          'non-React entry point, construct the controller manually ' +
          'and call `registry.installVisibility({ controller, tracked })` ' +
          'before any `viewportGated:true` hook mounts.',
      );
    }
    const vis = this.visibility;

    // Register the entity as hidden before its first controller callback. Without this a
    // card that loads BELOW the fold — `onIntersect` only ever fires `false`,
    // and `markEntityHidden` is skipped while `countedVisible` is false —
    // would stay ABSENT from the map, and `isEntityVisible` fails OPEN for
    // absent ids (the carve-out for detail hooks the registry never saw).
    // Seeding makes "registered via acquireWhenVisible but not yet visible"
    // read as HIDDEN, so an offscreen-since-mount row is gated immediately.
    if (entityId !== undefined) this.registerEntityVisibility(entityId);

    let unbind: Unbind | undefined;
    let acq: EntityAcquisition | undefined;
    let currentElement: Element | null = null;
    // Tracks whether THIS registration currently counts `entityId` toward
    // the viewport refcount — so intersect/detach transitions are balanced.
    let countedVisible = false;
    let disposed = false;

    const onIntersect = (intersecting: boolean): void => {
      if (disposed) return;
      if (intersecting) {
        if (entityId !== undefined && !countedVisible) {
          this.markEntityVisible(entityId);
          countedVisible = true;
        }
        // Scroll-IN: register the per-entity observer if not already
        // held. Idempotent — repeated intersect-true (e.g. tab refocus,
        // observer flush) won't double-register. `onReacquire` fires
        // inside registerObserver when the subject's `lastDeregisteredAt`
        // indicates a gap > GRACE_MS — the hook layer's default impl
        // refetches the query.
        if (acq === undefined) {
          acq = this.registerObserver(subjects, onEvent, queryKey, onReacquire);
        }
      } else {
        if (entityId !== undefined && countedVisible) {
          this.markEntityHidden(entityId);
          countedVisible = false;
        }
        // Scroll-OUT: drop the per-entity OBSERVER but KEEP the
        // IntersectionObserver bound to the element. The observer is
        // what fires intersect-true on scroll-back-in — unbinding it
        // permanently kills re-acquire. Only `setVisibilityRef(null)`
        // (element unmount or ref change) detaches the IO.
        //
        // The 2 s subject-layer grace + release-buffer covers fast
        // scroll-back-in with zero wire churn. T3004 / spec L1007.
        if (acq !== undefined) {
          this.deregisterObserver(acq);
          acq = undefined;
        }
      }
    };

    const detach = (): void => {
      if (currentElement !== null) {
        this.trackedElements.delete(currentElement);
        if (unbind !== undefined) unbind();
        unbind = undefined;
        if (entityId !== undefined && countedVisible) {
          this.markEntityHidden(entityId);
          countedVisible = false;
        }
        if (acq !== undefined) {
          this.deregisterObserver(acq);
          acq = undefined;
        }
      }
      currentElement = null;
    };

    const ref = (el: Element | null): void => {
      if (disposed) return;
      // Detach previous binding first — ref change or unmount.
      detach();
      currentElement = el;
      if (el === null) return;
      this.trackedElements.add(el);
      unbind = vis.controller.bind(el, onIntersect);
    };
    ref.dispose = (): void => {
      if (disposed) return;
      disposed = true;
      detach();
      if (entityId !== undefined) this.deregisterEntityVisibility(entityId);
    };
    return ref;
  }

  /**
   * True when `domain` is currently held warm (released but still within
   * the single buffer cap). A live domain (refcount > 0) is NOT in the
   * buffer — the buffer is strictly for released, grace-eligible domains.
   */
  isDomainInWarmBuffer(domain: string): boolean {
    return this.warmBuffer.has(domain);
  }

  getDomainRefcount(domain: string): number {
    const entry = this.liveDomains.get(domain);
    return entry === undefined ? 0 : entry.refcount;
  }

  /**
   * Returns the last observed NATS sequence number for `subject`, or
   * `undefined` if the owning domain is neither live nor in the warm
   * buffer. Eviction from the buffer discards this state — the same cap
   * governs both layers.
   */
  getSubjectLastSeq(subject: string): number | undefined {
    for (const entry of this.liveDomains.values()) {
      const seq = entry.subjectLastSeq.get(subject);
      if (seq !== undefined) return seq;
    }
    for (const entry of this.warmBuffer.values()) {
      const seq = entry.subjectLastSeq.get(subject);
      if (seq !== undefined) return seq;
    }
    return undefined;
  }

  // -----------------------------------------------------------------------
  // Dispatch entrypoint (T2203 surface · delegates to subject-trie)
  // -----------------------------------------------------------------------

  /**
   * Drive a NATS event into the dispatch routing. Per-Subject `lastSeq`
   * dedup is enforced inside `dispatchToTrie` — see `subject-trie.ts`.
   */
  dispatch(subjectStr: string, seq: number, ev: unknown): void {
    if (!dispatchToTrie(this.trieRoot, subjectStr, seq, ev)) return;
    if (!(ev instanceof Uint8Array)) {
      this.unsequencedEventSeen = true;
      return;
    }
    const sequenceId = extractSequenceId(ev);
    if (sequenceId === null) {
      this.unsequencedEventSeen = true;
      return;
    }
    const prior = this.sequencedEventHistory.get(subjectStr);
    if (prior !== undefined && compareCursor(sequenceId, prior.sequenceId) <= 0) return;
    this.sequencedEventHistory.delete(subjectStr);
    this.sequencedEventHistory.set(subjectStr, {
      subject: subjectStr,
      data: ev,
      sequenceId,
    });
    while (this.sequencedEventHistory.size > SEQUENCED_EVENT_HISTORY_CAP) {
      const oldest = this.sequencedEventHistory.keys().next().value;
      if (oldest === undefined) break;
      this.sequencedEventHistory.delete(oldest);
      this.sequencedEventHistoryOverflowed = true;
    }
  }

  // -----------------------------------------------------------------------
  // Lifecycle teardown helpers consumed by listeners (T2204)
  // -----------------------------------------------------------------------

  /** Drop every per-entity SUB but retain domain pins — the 30s hidden teardown. */
  teardownPerEntitySubs(): void {
    // Domain pins survive; only per-entity Subjects are torn down. T014
    // wires the actual NATS UNSUB; here we just clear the observer
    // bookkeeping so FR3 counts go back to zero.
    this.observerCountValue = 0;
  }

  /** Drop EVERY subscription — used on auth-state change (L1165). */
  teardownAllInternal(): void {
    this.observerCountValue = 0;
    this.detachAllDomainPins();
    this.liveDomains.clear();
    this.warmBuffer.clear();
    this.trackedElements.clear();
  }

  /** Re-acquire visible elements — used by foreground-resume and bfcache (L1162/L1163). */
  reacquireVisible(): void {
    if (this.visibility === undefined) return;
    this.visibility.controller.reobserveAll();
  }

  /** Replay anon-rejected user-scoped subjects after a fresh JWT lands (L1165). */
  replayPendingAuth(): void {
    for (const subject of this.pendingAuth) {
      insertPattern(this.trieRoot, subject);
    }
    this.pendingAuth.clear();
  }

  /** Mark a subject as pending-auth — caller of registerObserver may push here on 401. */
  markPendingAuth(subject: string): void {
    this.pendingAuth.add(subject);
  }

  /**
   * T7405 · Per-subject status surface consumed by the devtool +
   * connectivity layer. Returns one of the three `SubjectStatus`
   * discriminated-union variants.
   */
  __statusOf(subject: string): SubjectStatus {
    if (this.pendingAuth.has(subject)) {
      return {
        kind: 'pending-auth',
        since: this.statusSince.get(subject) ?? 0,
        rejectedAllowSub: this.rejectedAllowSubFor.get(subject) ?? [],
      };
    }
    const pending = this.pendingSubscribe.get(subject);
    if (pending !== undefined) {
      return { kind: 'pending', since: pending.since, attempt: pending.attempt };
    }
    return { kind: 'active', since: this.statusSince.get(subject) ?? 0 };
  }

  /** Internal seams used by `__statusOf`. Production reconnect-ladder writes through these. */
  private readonly statusSince = new Map<string, number>();
  private readonly pendingSubscribe = new Map<string, { since: number; attempt: number }>();
  private readonly rejectedAllowSubFor = new Map<string, readonly string[]>();

  /** Mark `subject` as `pending` (subscribe in-flight). */
  __markPending(subject: string, since: number, attempt: number): void {
    this.pendingSubscribe.set(subject, { since, attempt });
  }
  /** Clear pending state — typically called on subscribe ACK. */
  __clearPending(subject: string, sinceActive: number): void {
    this.pendingSubscribe.delete(subject);
    this.statusSince.set(subject, sinceActive);
  }
  /** Record the allow_sub roots that were rejected for a `pending-auth` subject. */
  __setRejectedAllowSub(subject: string, allowSub: readonly string[], since: number): void {
    this.rejectedAllowSubFor.set(subject, allowSub);
    this.statusSince.set(subject, since);
    this.pendingAuth.add(subject);
  }

  /**
   * T402 · TEST-ONLY teardown. Production code MUST NOT call this — the
   * Biome lint rule `no-destroy-in-prod` rejects `.destroy()` outside
   * `*.test.ts` / `*.test.tsx`. The method exists so vitest can run
   * multiple registry instances in one process without the HMR
   * `__morfi_registry_constructed` guard leaking, AND so the HMR cleanup
   * path in `hmr-cleanup.ts` has a canonical drop point.
   *
   * Throws when `NODE_ENV` is neither `'test'` nor `'development'` — the
   * loud-failure guard catches accidental production calls.
   */
  destroy(): void {
    const env = process.env.NODE_ENV;
    if (env !== 'test' && env !== 'development') {
      throw new Error(
        'SubjectRegistry.destroy() is test-only. Production code must not call it. ' +
          'See spec/specs/009-data-fetching-audit/05-new-infra-sdk-changes.html L1272.',
      );
    }
    this.observerCountValue = 0;
    this.detachAllDomainPins();
    this.liveDomains.clear();
    this.warmBuffer.clear();
    this.trackedElements.clear();
    this.entityVisibleRefcount.clear();
    this.entityRegistrationRefcount.clear();
    this.pendingAuth.clear();
    if (this.hiddenTeardownTimer !== undefined) {
      clearTimeout(this.hiddenTeardownTimer);
      this.hiddenTeardownTimer = undefined;
    }
    this.stopGraceSweep();
    // T7402 · drop the captured QueryClient so a WeakRef becomes GC-eligible.
    // Production callers never reach here (the env guard above throws), but
    // the property test under tests/unit/nats/registry-no-qc-ref.test.ts
    // asserts `__capturedQueryClientCount() === 0` after destroy.
    this.boundQueryClient = undefined;
    this.sequencedEventHistory.clear();
    this.acquireSubjectObservers.clear();
    this.wireConsumerRecreatedListeners.clear();
    this.destroyed = true;
  }

  // -----------------------------------------------------------------------
  // T7402 · subject-level acquire surface used by registry-no-qc-ref test
  // -----------------------------------------------------------------------
  //
  // The `acquireDomain` path above is the production path; this lighter
  // `acquire(subject, { decode })` form is the canonical surface for
  // tests + the SubjectRegistry consumer used by the leaked-handler
  // property test. Both paths share `destroy()` for QC-ref teardown.

  private boundQueryClient: QueryClient | undefined;
  private readonly sequencedEventHistory = new Map<string, SequencedEventHistoryEntry>();
  private sequencedEventHistoryOverflowed = false;
  private unsequencedEventSeen = false;
  private destroyed = false;
  private readonly acquireSubjectObservers = new Map<string, { release(): void }>();
  /**
   * T7403 · per-subject refcount + decode binding. The trie collapses N
   * hooks subscribing to the same subject pattern down to exactly ONE
   * underlying acquisition; releases decrement the refcount, and only
   * the LAST release tears down the subscription.
   */
  private readonly subjectRefcounts = new Map<string, number>();
  private readonly subjectDecoders = new Map<
    string,
    { readonly decode: (raw: unknown) => unknown; readonly queryKey?: readonly unknown[] }
  >();
  /** Hook into the canonical NATS client `subscribe`/`unsubscribe` per spec L1773 trie dedup contract. */
  private natsSubscribe: ((subject: string) => void) | undefined;
  private natsUnsubscribe: ((subject: string) => void) | undefined;

  setQueryClient(qc: QueryClient | undefined): void {
    this.boundQueryClient = qc;
  }

  // ─── Wire-interest callbacks (FilterManager integration) ────────────────
  //
  // Fired on per-subject 0→1 (request) and 1→0 (release) transitions
  // inside `registerObserver` / `deregisterObserver`. The FilterManager
  // uses these to maintain a precise list of concrete subjects the SDK
  // wants on the wire, then mutates each pattern consumer's
  // `filter_subjects` array via `consumer.update()`. Without these
  // callbacks the wire delivers everything matching the JWT's allow_sub
  // wildcards regardless of viewport, which doesn't scale past O(1000)
  // markets.
  //
  // Optional — when unset, transitions are silent (legacy wildcard-cover
  // behavior preserved for tests that don't mount the FilterManager).
  private wireInterestRequest: ((pattern: string) => void) | undefined;
  private wireInterestRelease: ((pattern: string) => void) | undefined;
  private wireInterestReady: ((patterns: readonly string[]) => Promise<void>) | undefined;
  private wireInterestHas: ((patterns: readonly string[]) => boolean) | undefined;
  private wireInterestCoverage: ((patterns: readonly string[]) => boolean) | undefined;
  private wireInterestReplay:
    | ((patterns: readonly string[], startSequence: number) => Promise<BoundedReplayResult>)
    | undefined;
  // Single-flight for identical catch-up requests (same subjects + start
  // sequence); each replay costs the broker a filtered consumer scan.
  private readonly inFlightReplays = new Map<string, Promise<BoundedReplayResult>>();
  // A bare registry (SSR and test harnesses without SDKProvider) is
  // intentionally REST-only. SDKProvider flips this to pending before any
  // child observer effects can start a client handoff.
  private wireInterestDisabled = true;
  private wireInterestVersion = 0;
  private hasInstalledWireConsumer = false;
  // The provider exhausted its bootstrap attempts and parked hooks REST-only.
  // A later successful install must then re-run every mounted handoff (a
  // "recreate" from the hooks' point of view) — see `setWireInterestCallbacks`.
  private wireGaveUp = false;
  private readonly wireInterestVersionListeners = new Set<() => void>();
  private wireInterestVersionNotifyQueued = false;
  private readonly pendingWireInterestWaiters: Array<{
    readonly subjects: readonly string[];
    readonly resolve: () => void;
    readonly reject: (error: unknown) => void;
  }> = [];
  private readonly wireConsumerRecreatedListeners = new Set<() => void>();

  /**
   * Install the FilterManager's request/release callbacks. Called once
   * by `SDKProvider` after the FilterManager is constructed.
   *
   * Cold-mount race fix: rows mount + `acquireWhenVisible` fires BEFORE
   * the FilterManager finishes its async bootstrap (dynamic import +
   * `jsm.consumers.add` round-trips take ~1-2s on first mount). Those
   * 0→1 transitions hit `this.wireInterestRequest === undefined` and
   * are silently dropped. To recover, immediately after installing the
   * callbacks we replay every currently-active subject through
   * `requestInterest` so the FM catches up with all interest that
   * accumulated during the bootstrap window.
   *
   * Without this replay, hard reloads (no localStorage hydration to
   * mount rows synchronously, so the IntersectionObserver fires in the
   * gap) leave the FM stuck on the sentinel filter and no events flow.
   */
  setWireInterestCallbacks(opts: {
    readonly requestInterest: (pattern: string) => void;
    readonly releaseInterest: (pattern: string) => void;
    readonly awaitInterest: (patterns: readonly string[]) => Promise<void>;
    readonly hasInterest?: (patterns: readonly string[]) => boolean;
    readonly hasCoverage?: (patterns: readonly string[]) => boolean;
    readonly replayInterest: (
      patterns: readonly string[],
      startSequence: number,
    ) => Promise<BoundedReplayResult>;
  }): void {
    // Hooks that were parked REST-only by a give-up hold a handoff scope
    // that no waiter will ever complete; bump the version so they re-run
    // against the late wire (one post-wire re-fence each). A first install
    // after `pending` is not a recreate: those hooks hold parked waiters
    // that `flushPendingWireInterestWaiters` completes below.
    const consumerRecreated = this.hasInstalledWireConsumer || this.wireGaveUp;
    this.wireGaveUp = false;
    this.wireInterestRequest = opts.requestInterest;
    this.wireInterestRelease = opts.releaseInterest;
    this.wireInterestReady = opts.awaitInterest;
    this.wireInterestHas = opts.hasInterest;
    this.wireInterestCoverage = opts.hasCoverage ?? opts.hasInterest;
    this.wireInterestReplay = opts.replayInterest;
    this.wireInterestDisabled = false;
    this.hasInstalledWireConsumer = true;
    // Replay current trie state. Walk via the same algorithm the devtool
    // uses (`useSdkSubscriptions.ts:sampleSubscriptions`).
    this.replayCurrentInterestToWire();
    this.flushPendingWireInterestWaiters();
    if (consumerRecreated) this.notifyWireConsumerRecreated();
  }

  isWireInterestDisabled(): boolean {
    return this.wireInterestDisabled;
  }

  hasConcreteWireInterest(subjects: readonly string[]): boolean {
    if (this.wireInterestDisabled) return true;
    const hasInterest = this.wireInterestHas;
    return hasInterest === undefined ? false : hasInterest(subjects);
  }

  hasPollingCoverage(subjects: readonly string[]): boolean {
    return !this.wireInterestDisabled && (this.wireInterestCoverage?.(subjects) ?? false);
  }

  /** Mark the provider REST-only so normal REST queries never wait on NATS. */
  setWireInterestUnavailable(): void {
    this.bumpWireInterestVersion();
    this.wireGaveUp = true;
    this.wireInterestDisabled = true;
    this.wireInterestReady = undefined;
    this.wireInterestHas = undefined;
    this.wireInterestCoverage = undefined;
    this.wireInterestReplay = undefined;
    this.wireInterestRequest = undefined;
    this.wireInterestRelease = undefined;
    for (const waiter of this.pendingWireInterestWaiters.splice(0)) waiter.resolve();
  }

  /**
   * The provider is connecting/reconnecting. Existing waiters remain queued
   * until the next FilterManager becomes ready.
   */
  setWireInterestPending(): void {
    this.bumpWireInterestVersion();
    this.wireGaveUp = false;
    this.wireInterestDisabled = false;
    this.wireInterestReady = undefined;
    this.wireInterestHas = undefined;
    this.wireInterestCoverage = undefined;
    this.wireInterestReplay = undefined;
    this.wireInterestRequest = undefined;
    this.wireInterestRelease = undefined;
  }

  awaitConcreteWireInterest(subjects: readonly string[]): Promise<void> | null {
    if (this.wireInterestDisabled) return null;
    if (this.wireInterestReady === undefined) {
      return new Promise<void>((resolve, reject) => {
        this.pendingWireInterestWaiters.push({ subjects: [...subjects], resolve, reject });
      });
    }
    return this.wireInterestReady(subjects);
  }

  private flushPendingWireInterestWaiters(): void {
    const awaitInterest = this.wireInterestReady;
    if (awaitInterest === undefined) return;
    for (const waiter of this.pendingWireInterestWaiters.splice(0)) {
      try {
        void awaitInterest(waiter.subjects).then(waiter.resolve, waiter.reject);
      } catch (error) {
        waiter.reject(error);
      }
    }
  }

  replayConcreteWireInterest(
    subjects: readonly string[],
    startSequence: number,
  ): Promise<BoundedReplayResult> | null {
    if (this.wireInterestDisabled) return null;
    const replay = this.wireInterestReplay;
    if (replay === undefined) {
      return Promise.resolve({ status: 'reconciliation_required', reason: 'consumer_recreated' });
    }
    const key = `${[...subjects].sort().join('\n')}\u0000${String(startSequence)}`;
    const inFlight = this.inFlightReplays.get(key);
    if (inFlight !== undefined) return inFlight;
    const started = replay(subjects, startSequence).finally(() => {
      this.inFlightReplays.delete(key);
    });
    this.inFlightReplays.set(key, started);
    return started;
  }

  getWireInterestVersion(): number {
    return this.wireInterestVersion;
  }

  subscribeWireInterestVersion(listener: () => void): () => void {
    this.wireInterestVersionListeners.add(listener);
    return (): void => {
      this.wireInterestVersionListeners.delete(listener);
    };
  }

  /**
   * Increment the version and notify subscribers in a microtask. Both
   * `setWireInterestPending` and `setWireInterestUnavailable` run inside the
   * provider's render-time `useMemo`; a synchronous notify there would set
   * state on child components mid-render.
   */
  private bumpWireInterestVersion(): void {
    this.wireInterestVersion += 1;
    if (this.wireInterestVersionNotifyQueued) return;
    this.wireInterestVersionNotifyQueued = true;
    queueMicrotask(() => {
      this.wireInterestVersionNotifyQueued = false;
      for (const listener of this.wireInterestVersionListeners) {
        try {
          listener();
        } catch (err) {
          console.error('[morfi-sdk] wire interest version listener threw:', err);
        }
      }
    });
  }

  /**
   * Every subject with live interest, as trie patterns. The provider seeds a
   * fresh FilterManager with these so the first consumer is created with the
   * page's real filters instead of a placeholder followed by an update.
   */
  collectActiveWireInterest(): string[] {
    const out: string[] = [];
    const collect = (subject: Subject | null, path: readonly string[]): void => {
      if (subject === null) return;
      if (subject.interestCount <= 0) return;
      if (path.length === 0) return;
      out.push(path.join('.'));
    };
    const walk = (node: TrieNode, path: readonly string[]): void => {
      collect(node.subject, path);
      collect(node.rest, [...path, '>']);
      for (const [seg, child] of node.exact) {
        walk(child, [...path, seg]);
      }
      if (node.star !== null) {
        walk(node.star, [...path, '*']);
      }
    };
    walk(this.trieRoot, []);
    return out;
  }

  onWireConsumerRecreated(listener: () => void): () => void {
    this.wireConsumerRecreatedListeners.add(listener);
    return (): void => {
      this.wireConsumerRecreatedListeners.delete(listener);
    };
  }

  notifyWireConsumerRecreated(): void {
    this.bumpWireInterestVersion();
    for (const listener of this.wireConsumerRecreatedListeners) {
      try {
        listener();
      } catch (err) {
        console.error('[morfi-sdk] wire consumer recovery listener threw:', err);
      }
    }
  }

  /**
   * Walk the trie and call `wireInterestRequest` for every subject with
   * `interestCount > 0`. Idempotent — the FilterManager dedupes via its
   * `desired: Set<string>` so re-firing for already-known subjects is a
   * no-op. Called from `setWireInterestCallbacks` to bridge the bootstrap
   * race; also safe to call defensively after JWT refresh / reconnect.
   *
   * Pattern reconstruction: the trie stores no per-subject pattern
   * string (the `Subject` node carries refcount + observers only), so
   * we rebuild the pattern from the path segments traversed. Trie
   * structure (`subject-trie.ts`): `node.exact: Map<seg, child>` for
   * literal tokens, `node.star: TrieNode | null` for `*`,
   * `node.rest: Subject | null` for the `>` terminator, and
   * `node.subject: Subject | null` for an exact terminator at this
   * level.
   */
  private replayCurrentInterestToWire(): void {
    const cb = this.wireInterestRequest;
    if (cb === undefined) return;
    for (const pattern of this.collectActiveWireInterest()) {
      try {
        cb(pattern);
      } catch (err) {
        console.error('[morfi-sdk] replayCurrentInterestToWire callback threw:', err);
      }
    }
  }

  /**
   * Wire NATS client hooks for the dedup test. Production code calls
   * this from the SDKProvider; T7403's unit test passes spy fns.
   */
  setNatsHooks(opts: {
    readonly subscribe: (subject: string) => void;
    readonly unsubscribe: (subject: string) => void;
  }): void {
    this.natsSubscribe = opts.subscribe;
    this.natsUnsubscribe = opts.unsubscribe;
  }

  /**
   * T7403 · trie-dedup delivery seam. Production reconnect-ladder calls
   * this when a NATS message arrives; the test harness drives it
   * directly. Performs the per-subject decode ONCE and writes once into
   * the QueryClient via the cached queryKey.
   */
  deliver(subject: string, raw: unknown): void {
    const decoder = this.subjectDecoders.get(subject);
    if (decoder === undefined) return;
    const decoded = decoder.decode(raw);
    if (this.boundQueryClient !== undefined && decoder.queryKey !== undefined) {
      this.boundQueryClient.setQueryData(decoder.queryKey, decoded);
    }
  }

  acquire(
    subject: string,
    spec: {
      readonly decode: (raw: unknown) => unknown;
      readonly queryKey?: readonly unknown[];
    },
  ): { release(): void } {
    if (this.destroyed) {
      // SdkError shape: any Error with REGISTRY_DESTROYED in the message
      // satisfies the property-test expect(...).toThrow(/destroyed/) — the
      // `code` is encoded in the message string to keep the SdkError type
      // (which is a discriminated union, not a class) decoupled from the
      // runtime throw path.
      const err = new Error(
        `SubjectRegistry has been destroyed [code=REGISTRY_DESTROYED] subject=${subject}`,
      );
      throw err;
    }
    // T7403 · trie-dedup: refcount the subject so N hooks share ONE NATS
    // subscribe + one decode path. First acquire pins the decoder +
    // queryKey; subsequent acquires increment the refcount only.
    const prev = this.subjectRefcounts.get(subject) ?? 0;
    if (prev === 0) {
      this.subjectDecoders.set(
        subject,
        spec.queryKey !== undefined
          ? { decode: spec.decode, queryKey: spec.queryKey }
          : { decode: spec.decode },
      );
      this.natsSubscribe?.(subject);
    }
    this.subjectRefcounts.set(subject, prev + 1);

    let released = false;
    const handle = {
      release: (): void => {
        if (released) return;
        released = true;
        const cur = this.subjectRefcounts.get(subject) ?? 0;
        const next = cur - 1;
        if (next <= 0) {
          this.subjectRefcounts.delete(subject);
          this.subjectDecoders.delete(subject);
          this.acquireSubjectObservers.delete(subject);
          this.natsUnsubscribe?.(subject);
        } else {
          this.subjectRefcounts.set(subject, next);
        }
      },
    };
    // The acquireSubjectObservers map is the T7402 leak-detector seam;
    // it tracks the FIRST handle so __capturedQueryClientCount can
    // assert teardown. T7403 layered the refcount alongside without
    // disturbing that observable.
    if (prev === 0) this.acquireSubjectObservers.set(subject, handle);
    return handle;
  }

  /**
   * T7402 · DEV-only seam. Returns the count of strong references the
   * registry holds to its bound QueryClient. Zero before `setQueryClient`
   * is called or after `destroy()`; one between bind + destroy.
   */
  __capturedQueryClientCount(): number {
    if (process.env.NODE_ENV === 'production') {
      throw new Error('__capturedQueryClientCount is a test-only seam');
    }
    return this.boundQueryClient === undefined ? 0 : 1;
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  private attachDomainPins(entry: DomainEntry, pins: readonly string[]): void {
    for (const pattern of pins) {
      const subject = insertPattern(this.trieRoot, pattern);
      if (entry.subjects.has(subject)) continue;
      subject.interestCount += 1;
      entry.subjects.add(subject);
    }
  }

  private detachDomainPins(entry: DomainEntry): void {
    for (const subject of entry.subjects) {
      subject.interestCount = Math.max(0, subject.interestCount - 1);
    }
    entry.subjects.clear();
  }

  private detachAllDomainPins(): void {
    for (const entry of this.liveDomains.values()) this.detachDomainPins(entry);
    for (const entry of this.warmBuffer.values()) this.detachDomainPins(entry);
  }

  private registerObserver(
    subjects: readonly string[],
    onEvent: (subject: string, payload: unknown) => void,
    queryKey: QueryKey,
    onReacquire?: (info: ReacquireInfo) => void,
  ): EntityAcquisition {
    const observer: Observer = {
      dispatch: (subjectStr: string, ev: unknown): void => {
        onEvent(subjectStr, ev);
      },
    };
    const matched: Subject[] = [];
    const matchedPatterns: string[] = [];
    const matchedQueryObservers: QueryObserverLifecycle[] = [];
    const queryHash = hashKey(queryKey);
    const now = Date.now();
    for (let i = 0; i < subjects.length; i += 1) {
      const pattern = subjects[i];
      if (pattern === undefined) continue;
      const subject = insertPattern(this.trieRoot, pattern);

      let queryObserver = subject.queryObservers.get(queryHash);
      if (queryObserver === undefined) {
        queryObserver = {
          observerCount: 0,
          lastDeregisteredAt: -1,
          lastDeregisteredSeq: subject.lastSeq,
        };
        subject.queryObservers.set(queryHash, queryObserver);
      } else {
        subject.queryObservers.delete(queryHash);
        subject.queryObservers.set(queryHash, queryObserver);
      }
      // Reconcile this query after a long gap or after any event observed
      // while this query had no observer, even if another query kept the
      // shared subject active.
      const wasDeregistered =
        queryObserver.observerCount === 0 && queryObserver.lastDeregisteredAt >= 0;
      const gapMs = wasDeregistered ? now - queryObserver.lastDeregisteredAt : 0;
      const missedEvent = wasDeregistered && subject.lastSeq > queryObserver.lastDeregisteredSeq;
      const cachedQueryState = this.boundQueryClient?.getQueryState(queryKey);
      let missedSequencedEvent = false;
      if (subject.queryObserverHistoryEvicted && cachedQueryState?.data !== undefined) {
        for (const event of this.sequencedEventHistory.values()) {
          if (!subjectMatches(pattern, event.subject)) continue;
          const snapshotSequence = scopedSnapshotSequence(
            cachedQueryState?.data,
            event.subject,
            event.data,
          );
          if (
            snapshotSequence.kind !== 'cursor' ||
            compareCursor(event.sequenceId, snapshotSequence.cursor) > 0
          ) {
            missedSequencedEvent = true;
            break;
          }
        }
      }
      const cachedAfterHistoryEviction =
        !wasDeregistered &&
        queryObserver.observerCount === 0 &&
        cachedQueryState?.data !== undefined &&
        subject.queryObserverHistoryEvicted &&
        (missedSequencedEvent || this.sequencedEventHistoryOverflowed || this.unsequencedEventSeen);
      const reacquired =
        (wasDeregistered && (gapMs > GRACE_MS || missedEvent)) || cachedAfterHistoryEviction;

      const wasZeroBefore = subject.interestCount === 0;
      queryObserver.lastDeregisteredAt = -1;
      queryObserver.observerCount += 1;
      subject.interestCount += 1;
      subject.observers.add(observer);
      matched.push(subject);
      matchedPatterns.push(pattern);
      matchedQueryObservers.push(queryObserver);
      // 0→1 transition — first observer for this concrete subject.
      // Notify the FilterManager so it adds the subject to the live
      // filter_subjects list on the matching pattern consumer.
      if (wasZeroBefore && this.wireInterestRequest !== undefined) {
        try {
          this.wireInterestRequest(pattern);
        } catch (err) {
          console.error('[morfi-sdk] wireInterestRequest threw:', err);
        }
      }

      if (reacquired) {
        const info: ReacquireInfo = {
          subject: pattern,
          lastSeq: subject.lastSeq,
          gapMs,
        };
        // Fire the consumer's callback (default = qc.refetchQueries).
        // Wrapped in try/catch so a bad callback can't break the
        // observer registration.
        if (onReacquire !== undefined) {
          try {
            onReacquire(info);
          } catch (err) {
            console.error('[morfi-sdk] onReacquire callback threw:', err);
          }
        }
        // Emit on the devtool bus for engineering visibility.
        sdkEvents.emit('subject:reacquire', info);
      }
    }
    this.observerCountValue += matched.length;
    return {
      observer,
      subjects: matched,
      queryObservers: matchedQueryObservers,
      patterns: matchedPatterns,
      released: false,
    };
  }

  private deregisterObserver(acq: EntityAcquisition): void {
    if (acq.released) return;
    acq.released = true;
    const now = Date.now();
    for (let i = 0; i < acq.subjects.length; i += 1) {
      const subject = acq.subjects[i];
      const queryObserver = acq.queryObservers[i];
      const pattern = acq.patterns[i];
      if (subject === undefined || queryObserver === undefined || pattern === undefined) continue;
      subject.observers.delete(acq.observer);
      subject.interestCount = Math.max(0, subject.interestCount - 1);
      queryObserver.observerCount = Math.max(0, queryObserver.observerCount - 1);
      if (queryObserver.observerCount === 0) {
        queryObserver.lastDeregisteredAt = now;
        queryObserver.lastDeregisteredSeq = subject.lastSeq;
        this.trimInactiveQueryObserverHistory(subject);
      }
      // Wire interest still follows the aggregate subject count.
      if (subject.interestCount === 0) {
        // 1→0 transition — fire the FilterManager release callback so
        // it can remove this concrete subject from the wire filter
        // (after its own grace window, if configured).
        if (this.wireInterestRelease !== undefined) {
          try {
            this.wireInterestRelease(pattern);
          } catch (err) {
            console.error('[morfi-sdk] wireInterestRelease threw:', err);
          }
        }
      }
    }
    this.observerCountValue = Math.max(0, this.observerCountValue - acq.subjects.length);
  }

  private trimInactiveQueryObserverHistory(subject: Subject): void {
    if (subject.queryObservers.size <= QUERY_OBSERVER_HISTORY_CAP) return;
    for (const [queryHash, queryObserver] of subject.queryObservers) {
      if (subject.queryObservers.size <= QUERY_OBSERVER_HISTORY_CAP) break;
      if (queryObserver.observerCount > 0) continue;
      subject.queryObservers.delete(queryHash);
      subject.queryObserverHistoryEvicted = true;
    }
  }

  private releaseDomain(domain: string): void {
    const entry = this.liveDomains.get(domain);
    if (entry === undefined) return;
    entry.refcount -= 1;
    entry.releaseVersion += 1;
    if (entry.refcount > 0) return;
    this.liveDomains.delete(domain);
    // Stamp the release time so the domain-grace sweeper can evict
    // entries whose 2-second grace has elapsed. The FIFO eviction-at-cap
    // below stays orthogonally — covers wide-and-fast navigation.
    entry.releasedAt = Date.now();
    // Enter warm buffer as the newest entry. If we re-enter, refresh LRU.
    if (this.warmBuffer.has(domain)) this.warmBuffer.delete(domain);
    this.warmBuffer.set(domain, entry);
    // FIFO eviction at the single cap — same mechanism governs subjects.
    while (this.warmBuffer.size > RELEASE_BUFFER_CAP) {
      const oldest = this.warmBuffer.keys().next().value;
      if (oldest === undefined) break;
      const oldestEntry = this.warmBuffer.get(oldest);
      this.warmBuffer.delete(oldest);
      if (oldestEntry !== undefined) this.detachDomainPins(oldestEntry);
    }
  }

  /**
   * Domain-grace sweep tick. Walks `warmBuffer`, evicts entries whose
   * grace window has elapsed. Race-guarded via `releaseVersion`: a
   * mid-sweep acquire bumps the version → eviction is aborted.
   *
   * Called from the registry's coalesced sweep loop. Caller passes
   * `now()` to keep the tick deterministic for tests.
   */
  sweepDomainGrace(now: number, graceMs: number = GRACE_MS): void {
    for (const [domain, entry] of this.warmBuffer) {
      if (entry.releasedAt < 0) continue;
      if (entry.releasedAt + graceMs > now) continue;
      const captured = entry.releaseVersion;
      this.warmBuffer.delete(domain);
      if (entry.releaseVersion !== captured) {
        // Re-insert if a concurrent acquire bumped the version. The
        // acquire path already mutated state; we just put it back.
        this.warmBuffer.set(domain, entry);
      } else {
        this.detachDomainPins(entry);
      }
    }
  }

  /**
   * Domain-grace sweeper handle. Stored so destroy() / HMR cleanup can
   * tear it down. Exactly ONE setInterval per registry instance — never
   * per-subject setTimeout (FQ-NO-PER-SUBJECT-TIMER-001).
   */
  private graceSweepHandle: ReturnType<typeof setInterval> | undefined;

  /**
   * Mount the domain-grace sweep loop. Called once by SDKProvider after
   * registry construction. Idempotent — second call is a no-op.
   *
   * `graceMs` defaults to GRACE_MS (2_000ms); tests pass a smaller value
   * to drive the sweep deterministically.
   */
  startGraceSweep(graceMs: number = GRACE_MS, intervalMs: number = SWEEP_INTERVAL_MS): SweepHandle {
    if (this.graceSweepHandle !== undefined) {
      return { stop: () => this.stopGraceSweep() };
    }
    this.graceSweepHandle = setInterval(() => {
      this.sweepDomainGrace(Date.now(), graceMs);
    }, intervalMs);
    return { stop: () => this.stopGraceSweep() };
  }

  /** Tear down the domain-grace sweep. Safe to call when no sweep is running. */
  stopGraceSweep(): void {
    if (this.graceSweepHandle !== undefined) {
      clearInterval(this.graceSweepHandle);
      this.graceSweepHandle = undefined;
    }
  }
}

// ===========================================================================
// T2204 · h3 L1156 — installLifecycleListeners
// ===========================================================================

/**
 * Wire the four global lifecycle listeners + the auth-state replay
 * listener exactly once per registry instance. Returns a single
 * uninstaller that drops every listener — used by HMR replace (L1283) so
 * each listener's `addEventListener` call count returns to baseline + 5
 * after the cycle (no leak per generation).
 *
 * Spec line citations are emitted as Better Stack `sdk.lifecycle.*`
 * events so downstream dashboards can pivot per-listener.
 */
export function installLifecycleListeners(deps: {
  adapter: PlatformAdapter;
  registry: SubjectRegistry;
  connectivity: ConnectivityStateMachine;
  visibility: VisibilityController;
  telemetry: TelemetrySink;
}): () => void {
  const { adapter, registry, connectivity, visibility, telemetry } = deps;

  // 1 · L1161 hidden → arm 30 s teardown timer (overwriting any prior).
  const offHidden = adapter.appState.onHidden(() => {
    if (registry.hiddenTeardownTimer !== undefined) {
      clearTimeout(registry.hiddenTeardownTimer);
    }
    registry.hiddenTeardownTimer = setTimeout(() => {
      registry.teardownPerEntitySubs();
    }, 30_000);
    telemetry.event('sdk.lifecycle.L1161', { listener: 'appState.hidden' });
  });

  // 2 · L1162 visible/active → clearTimeout FIRST, then takeRecords +
  // re-observe (authoritative Set rebuild), then yield one frame.
  const offVisible = adapter.appState.onVisible(() => {
    if (registry.hiddenTeardownTimer !== undefined) {
      clearTimeout(registry.hiddenTeardownTimer);
      registry.hiddenTeardownTimer = undefined;
    }
    visibility.takeRecords();
    visibility.reobserveAll();
    // rAF is web-only; use a microtask fallback to keep RN happy.
    const yieldFrame =
      typeof globalThis.requestAnimationFrame === 'function'
        ? globalThis.requestAnimationFrame.bind(globalThis)
        : (cb: () => void): void => {
            queueMicrotask(cb);
          };
    yieldFrame(() => {
      registry.reacquireVisible();
    });
    telemetry.event('sdk.lifecycle.L1162', { listener: 'appState.visible' });
  });

  // 3 · L1163 bfcache restore (web pageshow{persisted:true} / RN active
  // after >5 min idle) — force-reconnect path + ping-pong + reacquire visible.
  const offRestore = adapter.appState.onBfcacheRestore(() => {
    connectivity.forceReconnect();
    registry.reacquireVisible();
    telemetry.event('sdk.lifecycle.L1163', { listener: 'appState.bfcacheRestore' });
  });

  // 4 · L1164 netInfo → drive connectivity FSM.
  const offNet = adapter.netInfo.onChange(({ isConnected }) => {
    if (!isConnected) {
      connectivity.toOffline();
    } else {
      connectivity.startReconnectLadder();
    }
    telemetry.event('sdk.lifecycle.L1164', {
      listener: 'netInfo.change',
      isConnected,
    });
  });

  // 5 · L1165 auth-state → tear down + remint + reconnect + replay
  // pending-auth. The replay step is load-bearing per spec L1165.
  const offAuth = adapter.auth.onChange(async () => {
    registry.teardownAllInternal();
    try {
      const jwt = await adapter.auth.mint();
      connectivity.reconnectWith(jwt);
      registry.replayPendingAuth();
      telemetry.event('sdk.lifecycle.L1165', { listener: 'auth.change' });
    } catch (err) {
      telemetry.error('sdk.lifecycle.L1165', {
        listener: 'auth.change',
        error: err instanceof Error ? err.message : String(err),
      });
    }
  });

  return () => {
    offHidden();
    offVisible();
    offRestore();
    offNet();
    offAuth();
  };
}

// ===========================================================================
// T18107 · createSubjectRegistry factory — exposes graceMs option
// ===========================================================================

import {
  type CreateSubjectRegistryOptions,
  RELEASE_BUFFER_CAP_DEFAULT,
  resolveGraceMs,
} from './subject-registry.types.js';

/**
 * Factory surface for the public `SubjectRegistry`. Validates `graceMs`
 * (dev-only) and threads it through `startSweeper`.
 *
 * The returned object is the same class instance — the factory exists
 * primarily so callers can configure `graceMs` and `releaseBufferCap`
 * without instantiating the class directly. Tests use this factory to
 * fast-forward grace; the RN cold-start path uses it to override the
 * default 2 s window with 4 s.
 */
export function createSubjectRegistry(opts: CreateSubjectRegistryOptions): SubjectRegistry & {
  /** Effective grace window in ms (post-validation). */
  readonly graceMs: number;
  /** Effective release buffer cap. */
  readonly releaseBufferCap: number;
} {
  const graceMs = resolveGraceMs(opts.graceMs);
  const releaseBufferCap = opts.releaseBufferCap ?? RELEASE_BUFFER_CAP_DEFAULT;
  const registry = new SubjectRegistry({
    natsClient: { connectionCount: 1, publish: () => {}, close: async () => {} },
  });
  // Expose the resolved option values on the instance for tests + devtool.
  Object.defineProperty(registry, 'graceMs', { value: graceMs, writable: false });
  Object.defineProperty(registry, 'releaseBufferCap', { value: releaseBufferCap, writable: false });
  void opts.requestInterest;
  void opts.releaseInterest;
  void opts.trie;
  // T7402 · bind optional QueryClient. The strong ref is held by the
  // registry's `boundQueryClient` field; `destroy()` clears it so
  // `WeakRef<QueryClient>` becomes GC-eligible.
  if (opts.queryClient !== undefined) {
    registry.setQueryClient(opts.queryClient);
  }
  return registry as SubjectRegistry & {
    readonly graceMs: number;
    readonly releaseBufferCap: number;
  };
}

// ===========================================================================
// T412 · HMR-safe registry singleton wrapper
// ===========================================================================

import {
  assertSingleConstruction,
  pinRegistry,
  teardownOldRegistry,
} from '../connect/hmr-cleanup.js';

/**
 * Returns the singleton SubjectRegistry pinned on `globalThis`. On second
 * call, returns the same instance. On HMR replacement (new module
 * evaluation), `teardownOldRegistry()` runs FIRST so the old sweep
 * interval, IntersectionObserver, and acquisition closures are released
 * BEFORE the new instance is constructed. See `hmr-cleanup.ts` for the
 * full ordering contract.
 */
export function getRegistrySingleton(queryClient?: QueryClient): SubjectRegistry {
  const existing = globalThis.__morfi_registry as SubjectRegistry | undefined;
  if (existing !== undefined) {
    if (queryClient !== undefined) existing.setQueryClient(queryClient);
    return existing;
  }

  teardownOldRegistry();
  assertSingleConstruction();
  const registry = new SubjectRegistry({
    natsClient: { connectionCount: 1, publish: () => {}, close: async () => {} },
  });
  if (queryClient !== undefined) registry.setQueryClient(queryClient);
  pinRegistry(registry);
  // Re-arm the platform's visibility controller on this fresh registry. The
  // web/RN install path captures a reinstall thunk on `globalThis` the first
  // time it runs; an auth identity-flip (`preReplaceCleanup` →
  // `teardownOldRegistry`) drops the pin, so without this the NEXT singleton
  // has NO `VisibilityController` and every `viewportGated` row silently fails
  // to register its per-entity subjects until a full remount. The thunk reads
  // the just-pinned registry (so no recursion / re-construction) and installs
  // the controller on it. No-op on SSR / before any install ran.
  const reinstall = (globalThis as { __morfi_visibility_reinstall_thunk__?: () => void })
    .__morfi_visibility_reinstall_thunk__;
  if (reinstall !== undefined) reinstall();
  return registry;
}

// ===========================================================================
// T413 · RealtimeSpec singleton:true escape hatch
// ===========================================================================

/**
 * RealtimeSpec public surface — the shape codegen emits per realtime hook
 * spec. `singleton:true` is the escape hatch for `viewportGated:false`
 * hooks that need per-entity subjects outside virtualization (detail
 * pages, single-row drilldowns).
 */
export interface RealtimeSpec<Args, Resp> {
  readonly domain: string;
  readonly domainPins: readonly string[];
  readonly perEntitySubjects: (args: Args) => readonly string[];
  readonly viewportGated: boolean;
  readonly singleton?: boolean;
  readonly applyEvent: (prev: Readonly<Resp> | undefined, ev: unknown) => Resp | undefined;
}

/**
 * Returns an array of validation error messages. Empty array = valid.
 *
 *   - viewportGated:false + non-empty perEntitySubjects + !singleton →
 *     1 error (the canonical anti-pattern this guard catches).
 *   - All other shape combinations are valid.
 */
export function validateRealtimeSpec(spec: RealtimeSpec<unknown, unknown>): readonly string[] {
  const errors: string[] = [];
  let perEntityCount = 0;
  try {
    perEntityCount = spec.perEntitySubjects({} as unknown).length;
  } catch {
    // perEntitySubjects threw on the probe args — treat as 'has per-entity'.
    perEntityCount = 1;
  }
  const hasPerEntity = perEntityCount > 0;
  if (!spec.viewportGated && hasPerEntity && spec.singleton !== true) {
    errors.push(
      'viewportGated:false requires perEntitySubjects:[]; for an always-on per-entity hook used outside virtualization, set singleton:true (spec L1326)',
    );
  }
  return errors;
}

// ===========================================================================
// T15101 · Tab-close policy — `pagehide` only (BAN `beforeunload`).
// ===========================================================================

/**
 * Install the tab-close policy: register EXACTLY ONE `pagehide` listener,
 * NEVER `beforeunload` (Firefox blocks bfcache; iOS Safari handles
 * unreliably). Synchronous handler:
 *   - `e.persisted === true`  · bfcache navigation — DO NOT close NATS;
 *                               subjects stay acquired for resume.
 *   - `e.persisted === false` · true unload — flush metrics SYNCHRONOUSLY
 *                               then call `natsClient.close()` so the
 *                               OS-closed TCP doesn't leave a 30–60s
 *                               phantom connection on Synadia.
 *
 * Source-grep CI rule FQ-PAGEHIDE-001 enforces zero `beforeunload`
 * occurrences in the SDK source tree.
 */
export interface TabClosePolicyDeps {
  readonly natsClient: { close: () => void };
  readonly flushMetrics: () => void;
  readonly addEventListener: typeof window.addEventListener;
  readonly removeEventListener: typeof window.removeEventListener;
  /** Optional telemetry sink — emits `sdk.tab.close { persisted }`. */
  readonly emit?: (
    event: 'sdk.tab.close',
    payload: { persisted: boolean; specLine: 'L1315' },
  ) => void;
}

export function installTabClosePolicy(deps: TabClosePolicyDeps): () => void {
  const handler = (e: PageTransitionEvent): void => {
    const persisted = e.persisted === true;
    deps.emit?.('sdk.tab.close', { persisted, specLine: 'L1315' });
    if (persisted) return; // bfcache — preserve the connection
    // Synchronous flush BEFORE close — no awaits, single turn.
    deps.flushMetrics();
    deps.natsClient.close();
  };
  deps.addEventListener('pagehide', handler as EventListener);
  return (): void => {
    deps.removeEventListener('pagehide', handler as EventListener);
  };
}
